import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Button, Spin, Typography, Tabs, Modal, Collapse, Progress, Tooltip, message as antMessage, Dropdown, Popover } from 'antd';
import { SendOutlined, UserOutlined, RobotOutlined, ClockCircleOutlined, CodeOutlined, FullscreenOutlined, CopyOutlined, DownloadOutlined, CaretRightOutlined, WarningOutlined, PlusSquareOutlined, AppstoreOutlined, RightOutlined, BulbOutlined, CloseOutlined, DownOutlined, LikeOutlined, LikeFilled, DislikeOutlined, DislikeFilled, CheckCircleOutlined, CheckCircleFilled, SafetyCertificateOutlined, FileSearchOutlined, DatabaseOutlined, FundOutlined, TableOutlined, BarChartOutlined, MoreOutlined, ApiOutlined, DeploymentUnitOutlined, ShareAltOutlined, DashboardOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import vegaEmbed from 'vega-embed';
import { useAuth } from '../contexts/AuthContext';
import { submitDataFeedbackType, markDataQAAnswer } from '../services/dataService';
import MermaidViewer from './MermaidViewer';
import QuickStatsPanel from './QuickStatsPanel';
import AdvancedTable from './AdvancedTable';
import PromptLibraryDialog from './PromptLibraryDialog';
import promptTemplatesService from '../services/promptTemplatesService';
import { FilterableTable, FilterableChart, SqlWhereFilterBar } from './DeliveryFilters';
import FilterablePlotly from './FilterablePlotly';
import { executeDashboardSQL } from '../services/dashboardService';

/**
 * Detect a Plotly figure: `{data: [{type: 'scatter'|'pie'|...}, ...], layout?: {...}}`.
 * Vega-Lite specs use `mark` and `encoding` instead, so this is a clean
 * disambiguator at render time.
 */
function isPlotlyFigure(spec) {
  if (!spec || typeof spec !== 'object' || Array.isArray(spec)) return false;
  if (!Array.isArray(spec.data)) return false;
  const PLOTLY_TYPES = new Set([
    'scatter', 'scatter3d', 'scattergl', 'scattergeo', 'scattermapbox',
    'bar', 'pie', 'histogram', 'box', 'heatmap', 'mesh3d', 'surface',
    'sunburst', 'treemap', 'funnel', 'waterfall', 'choropleth',
  ]);
  return spec.data.some(t => t && typeof t === 'object' && PLOTLY_TYPES.has(t.type));
}

const { TextArea } = Input;
const { Text } = Typography;

/** Renders a Vega-Lite chart inside the fullscreen modal. */
const FullscreenChartRenderer = ({ spec, shouldRender }) => {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current || !spec || !shouldRender) return;
    const s = typeof spec === 'string' ? JSON.parse(spec) : spec;
    const fullSpec = { ...s, width: 'container', height: 500 };
    vegaEmbed(containerRef.current, fullSpec, {
      actions: { export: true, source: false, compiled: false, editor: false }
    }).catch(err => {
      if (containerRef.current) containerRef.current.innerHTML = `<div style="color:#ff4d4f;padding:12px">Chart error: ${err.message}</div>`;
    });
  }, [spec, shouldRender]);
  return <div ref={containerRef} style={{ width: '100%', minHeight: 520 }} />;
};

/**
 * Wrapper around AdvancedTable that adds a SqlWhereFilterBar.
 * Manages its own data state so WHERE filter changes can re-fetch fresh rows.
 * Accepts optional shared filter state props for syncing across tabs.
 */
function DeliveryTableWithFilters({ initialData, sqlQuery, filterValues, onFilterValuesChange }) {
  const [data, setData] = React.useState(initialData);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => { setData(initialData); }, [initialData]);
  return (
    <div>
      <SqlWhereFilterBar sqlQuery={sqlQuery} data={data} onData={setData} onLoading={setLoading}
        externalFilterValues={filterValues} onFilterValuesChange={onFilterValuesChange} />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Refreshing…</span>
        </div>
      ) : (
        <AdvancedTable data={data} dataSummary={data?.data_summary} maxRows={200} />
      )}
    </div>
  );
}

/**
 * Adds a SqlWhereFilterBar above an AdvancedTable so WHERE chips appear on
 * table-only deliveries. Without this, table-only Final Answers had no way
 * to expose / edit the WHERE clause inline (the filter bar used to live
 * only on chart deliveries via DeliveryChartWithFilters).
 */
function DataTableWithWhereFilter({ initialData, sqlQuery, dataSummary, maxRows, filterValues, onFilterValuesChange }) {
  const [data, setData] = React.useState(initialData);
  const [loading, setLoading] = React.useState(false);
  // Re-sync local state when the parent message gets a fresh table payload.
  React.useEffect(() => { setData(initialData); }, [initialData]);
  return (
    <div>
      {sqlQuery && (
        <SqlWhereFilterBar sqlQuery={sqlQuery} data={data} onData={setData} onLoading={setLoading}
          externalFilterValues={filterValues} onFilterValuesChange={onFilterValuesChange} />
      )}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Refreshing…</span>
        </div>
      ) : (
        <AdvancedTable data={data} dataSummary={dataSummary} maxRows={maxRows} />
      )}
    </div>
  );
}

/**
 * Wrapper around FilterableChart that adds a SqlWhereFilterBar.
 * Manages its own table-data state so WHERE filter changes re-fetch and re-render the chart.
 * Accepts optional shared filter state props for syncing across tabs.
 */
function DeliveryChartWithFilters({ chartSpec, initialData, sqlQuery, filterValues, onFilterValuesChange }) {
  const [data, setData] = React.useState(initialData);
  const [loading, setLoading] = React.useState(false);

  // The agent sometimes ships a chart artifact without a paired
  // table (e.g. "create_dashboard_component" only echoes the chart spec
  // + SQL — no rows). When that happens, `initialData` is empty and
  // `SqlWhereFilterBar` only fetches on user filter changes, so the
  // chart container renders blank until someone touches a chip. Fetch
  // once on mount when we have SQL but no rows.
  React.useEffect(() => {
    const hasRows = !!(initialData && initialData.columns && initialData.rows && initialData.rows.length);
    if (hasRows || !sqlQuery) return undefined;
    let cancelled = false;
    setLoading(true);
    executeDashboardSQL(sqlQuery, 200)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data || res);
      })
      .catch((err) => {
        if (!cancelled) console.error('[DeliveryChartWithFilters] auto-fetch failed:', String(err?.message || err).replace(/[\r\n]/g, ' '));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sqlQuery, initialData]);

  return (
    <div>
      <SqlWhereFilterBar sqlQuery={sqlQuery} data={data} onData={setData} onLoading={setLoading}
        externalFilterValues={filterValues} onFilterValuesChange={onFilterValuesChange} />
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>Refreshing…</span>
        </div>
      ) : (
        <FilterableChart chartSpec={chartSpec} tableData={data} />
      )}
    </div>
  );
}

/**
 * Reusable Chat Interface Component
 * @param {Function} onSendMessage - Callback when user sends a message (question, conversationHistory, onMessage, onError, onComplete)
 * @param {string} placeholder - Input placeholder text
 * @param {string} title - Chat title
 * @param {string} agentTitle - Agent title shown on welcome screen
 * @param {Function} onSaveQA - Optional callback to save Q&A to history (question, answer) => Promise
 * @param {Function} onLogInteraction - Optional callback to log interaction metrics ({ user_message, assistant_response, input_tokens, output_tokens, response_time_ms, status })
 */
const ChatInterface = forwardRef(({ onSendMessage, placeholder = 'Ask a question...', title = 'Chat', agentTitle = 'INSIGHT AGENT', onSaveQA = null, onLogInteraction = null, onAddToDashboard = null, onDashboardUpdate = null, onAddDashboardFilter = null, onUpdateDashboardFilter = null, onAddDashboardTab = null, onRenameDashboardTab = null, onSetDefaultDashboardTab = null, onChartEditorEdit = null, focusedComponent = null, onClearFocus = null, headerExtra = null, dashboards = [], activeDashboardId = null, onCreateDashboard = null, onCreateDashboardByName = null, onOpenDashboardByName = null, isDashboardOpen = false, onToggleDashboard = null, chatMode = 'normal', filterEditorContext = null }, ref) => {
  const { userEmail, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isChatMode = searchParams.get('mode') === 'chat';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [toolExecutions, setToolExecutions] = useState([]);
  const [messageTabs, setMessageTabs] = useState({}); // Track active tab per message
  const [streamingTab, setStreamingTab] = useState('progress'); // Tab for streaming message
  const [streamingMetrics, setStreamingMetrics] = useState({ inputTokens: 0, outputTokens: 0 });
  const [elapsedTime, setElapsedTime] = useState(0);
  // Shared WHERE filter state per message — syncs filters across Artifacts and Final Answer tabs
  const [messageFilterValues, setMessageFilterValues] = useState({});

  // Feedback state per message (keyed by message index): { feedbackType: 'like'|'dislike'|null, verified: bool, qaRecordId: number }
  const [messageFeedback, setMessageFeedback] = useState({});

  // Pending-feedback tracker (TC-AF-EC-01): keyed by message index → true while a
  // like/dislike/verify POST is in flight. Used to disable buttons so rapid
  // clicks don't send duplicate requests to the backend.
  const [feedbackPending, setFeedbackPending] = useState({});

  // Prompt Library state
  const [promptLibraryVisible, setPromptLibraryVisible] = useState(false);
  const [homepageTemplates, setHomepageTemplates] = useState([]);
  const [publishedFolders, setPublishedFolders] = useState([]);
  const [openFolderPopover, setOpenFolderPopover] = useState(null);

  // Per-folder iconic emoji shown next to the folder name on the chat
  // welcome screen and in the prompt-library popovers. Keep in sync with
  // FOLDER_ICONS in PromptLibraryDialog.js.
  const FOLDER_ICONS = {
    'Workload Catalog': '📦',
    'Workforce Management': '👥',
    'Business & Product Catalog': '🏢',
    'Technical Catalog': '🛠️',
    'ITSM': '🎫',
    'Delivery Plan': '🗓️',
    'Source Code': '🌿',
    'Application': '🧩',
    'Cloud Infrastructure': '☁️',
    'Compute & OS': '🖥️',
    'Software Inventory': '💿',
    'Network': '🌐',
    'Cloud Security': '🛡️',
    'Observability': '🔭',
    'Architecture & Readiness Reviews': '🏛️',
    'AI Usage': '🤖',
  };
  const PROMPT_COLORS = ['#d4380d', '#096dd9', '#389e0d', '#722ed1', '#c41d7f', '#d46b08', '#08979c'];

  // Load homepage templates and published folders for the welcome screen
  useEffect(() => {
    const loadHomepageTemplates = async () => {
      try {
        const [all, folders] = await Promise.all([
          promptTemplatesService.listTemplates(null),
          promptTemplatesService.listFolders(false),
        ]);
        const pinnedTemplates = all.filter(t => t.show_in_homepage);
        setHomepageTemplates(pinnedTemplates);
        setPublishedFolders(folders);
      } catch (e) {
        // Silently fail — templates are optional on the welcome screen
      }
    };
    loadHomepageTemplates();
  }, []);

  // Helper to render "Add to Dashboard" button with smart behavior.
  // Supports icon-only mode for the streamlined artifact toolbar
  // (matches the dashboard-card pattern: just a couple of icons in the
  // header, secondary actions in a More menu).
  const renderAddToDashboardBtn = (item, opts = {}) => {
    if (!onAddToDashboard) return null;
    const { iconOnly = false } = (typeof opts === 'object' && opts !== null) ? opts : {};
    // Inject default grid size when the caller didn't supply one.
    // The 12-col grid uses (6×6) as the comfortable "half-width, six-row"
    // tile that matches the chart-editor save default. Without this,
    // call sites that omit grid_w/grid_h fall back to the backend
    // default of (6×4) which renders charts uncomfortably squat — and
    // some legacy paths even produced (1×1) tiles in the top-left.
    const sizedItem = {
      grid_w: 6,
      grid_h: 6,
      ...item,
    };
    // Dashboard is open AND has an active dashboard → add directly
    if (isDashboardOpen && activeDashboardId && dashboards.length > 0) {
      const activeDash = dashboards.find(d => d.id === activeDashboardId);
      const onClick = () => {
        onAddToDashboard({ ...sizedItem, dashboard_id: activeDashboardId });
        antMessage.success(`Added to "${activeDash?.name || 'Dashboard'}"`);
      };
      if (iconOnly) {
        return (
          <Tooltip title={`Move to "${activeDash?.name || 'Dashboard'}"`}>
            <Button type="text" size="small" icon={<PlusSquareOutlined />} onClick={onClick} />
          </Tooltip>
        );
      }
      return (
        <Button size="small" icon={<PlusSquareOutlined />} onClick={onClick}>Move to Dashboard</Button>
      );
    }
    // Dashboard is hidden OR no dashboards exist → show dropdown with dashboard list
    const menuItems = [
      ...dashboards.map(d => ({
        key: String(d.id),
        icon: <AppstoreOutlined />,
        label: `${d.name}${d.is_default ? ' ⭐' : ''}`,
        onClick: () => {
          onAddToDashboard({ ...sizedItem, dashboard_id: d.id });
          antMessage.success(`Added to "${d.name}"`);
        },
      })),
      ...(onCreateDashboard ? [
        ...(dashboards.length > 0 ? [{ type: 'divider' }] : []),
        { key: 'new', icon: <PlusSquareOutlined />, label: 'New Dashboard…', onClick: async () => {
          const created = await onCreateDashboard();
          if (created) {
            onAddToDashboard({ ...sizedItem, dashboard_id: created.id });
            antMessage.success(`Added to "${created.name}"`);
          }
        }},
      ] : []),
    ];
    return (
      <Dropdown trigger={['click']} menu={{ items: menuItems }}>
        {iconOnly
          ? (
            <Tooltip title="Move to Dashboard">
              <Button type="text" size="small" icon={<PlusSquareOutlined />} />
            </Tooltip>
          )
          : <Button size="small" icon={<PlusSquareOutlined />}>Move to Dashboard ▾</Button>}
      </Dropdown>
    );
  };
  const [chartJsonModal, setChartJsonModal] = useState({ visible: false, spec: null });
  const [fullscreenModal, setFullscreenModal] = useState({ visible: false, type: null, content: null, title: '' });
  // Per-table / per-chart attachment modals (Quick Stats, SQL, underlying table)
  const [statsModal, setStatsModal] = useState({ visible: false, dataSummary: null, sanityWarnings: null, title: '' });
  const [sqlModal, setSqlModal] = useState({ visible: false, sql: '', title: '' });
  const [tableInChartModal, setTableInChartModal] = useState({ visible: false, table: null, title: '' });
  const [fullscreenChartReady, setFullscreenChartReady] = useState(false);
  const [confidenceModal, setConfidenceModal] = useState({ visible: false, confidence: null, breakdown: null });
  const processStartTimeRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Expose reset and loadConversation functions to parent components
  useImperativeHandle(ref, () => ({
    reset: () => {
      setMessages([]);
      setInput('');
      setIsLoading(false);
      setCurrentResponse('');
      setToolExecutions([]);
      setMessageTabs({});
      setStreamingTab('progress');
      setStreamingMetrics({ inputTokens: 0, outputTokens: 0 });
      setElapsedTime(0);
      setMessageFeedback({});
      processStartTimeRef.current = null;
    },
    loadConversation: (chatMessages) => {
      setMessages(chatMessages);
      setInput('');
      setIsLoading(false);
      setCurrentResponse('');
      setToolExecutions([]);
      setStreamingTab('progress');
      setStreamingMetrics({ inputTokens: 0, outputTokens: 0 });
      setElapsedTime(0);
      processStartTimeRef.current = null;
      // Initialize feedback state for loaded messages
      const fb = {};
      chatMessages.forEach((msg, idx) => {
        if (msg.role === 'assistant' && msg.qaRecordId) {
          fb[idx] = {
            feedbackType: msg.feedbackType || null,
            verified: !!msg.verifiedByPic,
            qaRecordId: msg.qaRecordId,
          };
        }
      });
      setMessageFeedback(fb);
      // Auto-switch all assistant messages to answer tab
      const tabs = {};
      chatMessages.forEach((msg, idx) => {
        if (msg.role === 'assistant') tabs[idx] = 'answer';
      });
      setMessageTabs(tabs);
    },
  }));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentResponse, toolExecutions]);

  // Timer for elapsed time during streaming
  useEffect(() => {
    let timer;
    if (isLoading && processStartTimeRef.current) {
      timer = setInterval(() => {
        setElapsedTime(Date.now() - processStartTimeRef.current);
      }, 100);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isLoading]);

  // Render Vega-Lite charts when messages change
  useEffect(() => {
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      messages.forEach((message, msgIdx) => {
        if (message.role === 'assistant' && message.tools) {
          message.tools.forEach((tool, toolIdx) => {
            if (tool.result) {
              const result = tool.result;
              let chartSpec = null;
              
              // Extract chart spec from various paths (matching Artifacts aggregation)
              if (result.chart) chartSpec = result.chart;
              else if (result.vega_spec) chartSpec = result.vega_spec;
              else if (result.vega_lite_spec) chartSpec = result.vega_lite_spec;
              else if (result.data?.vega_lite_spec) chartSpec = result.data.vega_lite_spec;
              else if (result.data?.chart) chartSpec = result.data.chart;
              else if (result.metadata?.chart) chartSpec = result.metadata.chart;
              
              if (chartSpec) {
                // Render chart in Artifacts tab
                const chartIdDelivery = `vega-chart-msg-${msgIdx}`;
                const elementDelivery = document.getElementById(chartIdDelivery);
                
                if (elementDelivery && elementDelivery.children.length === 0) {
                  try {
                    const spec = typeof chartSpec === 'string' ? JSON.parse(chartSpec) : chartSpec;
                    vegaEmbed(`#${chartIdDelivery}`, spec, {
                      actions: { export: true, source: false, compiled: false, editor: false }
                    }).catch(err => {
                      console.error('Vega-Lite rendering error (Delivery):', err);
                      elementDelivery.innerHTML = `<div style="color: #ff4d4f; padding: 12px;">Failed to render chart: ${err.message}</div>`;
                    });
                  } catch (err) {
                    console.error('Chart spec parse error (Delivery):', err);
                    elementDelivery.innerHTML = `<div style="color: #ff4d4f; padding: 12px;">Invalid chart specification</div>`;
                  }
                }
                
                // Render chart in Final Answer tab
                const chartIdAnswer = `vega-chart-answer-${msgIdx}`;
                const elementAnswer = document.getElementById(chartIdAnswer);
                
                if (elementAnswer && elementAnswer.children.length === 0) {
                  try {
                    const spec = typeof chartSpec === 'string' ? JSON.parse(chartSpec) : chartSpec;
                    vegaEmbed(`#${chartIdAnswer}`, spec, {
                      actions: { export: true, source: false, compiled: false, editor: false }
                    }).catch(err => {
                      console.error('Vega-Lite rendering error (Answer):', err);
                      elementAnswer.innerHTML = `<div style="color: #ff4d4f; padding: 12px;">Failed to render chart: ${err.message}</div>`;
                    });
                  } catch (err) {
                    console.error('Chart spec parse error (Answer):', err);
                    elementAnswer.innerHTML = `<div style="color: #ff4d4f; padding: 12px;">Invalid chart specification</div>`;
                  }
                }
              }
            }
          });
        }
      });
    }, 100);
    
    return () => clearTimeout(timer);
  }, [messages, messageTabs]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setCurrentResponse('');
    setToolExecutions([]);
    processStartTimeRef.current = Date.now();
    setElapsedTime(0);
    setStreamingMetrics({ inputTokens: 0, outputTokens: 0 });

    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    // Build conversation history from previous messages (format for backend)
    // For assistant messages, enrich with prior execution context (SQL, columns, chart)
    // so the agent can skip redundant catalog discovery on follow-up questions
    const conversationHistory = messages.map(msg => {
      if (msg.role === 'assistant' && msg.tools && msg.tools.length > 0) {
        let enrichedContent = msg.content || '';
        const contextLines = [];

        for (const tool of msg.tools) {
          if (tool.name === 'fetch_data' && tool.result) {
            try {
              const res = typeof tool.result === 'string' ? JSON.parse(tool.result) : tool.result;
              if (tool.input?.sql) contextLines.push(`SQL executed: ${tool.input.sql}`);
              if (res?.data?.columns) contextLines.push(`Data columns: ${JSON.stringify(res.data.columns)}`);
              if (res?.data?.row_count != null) contextLines.push(`Row count: ${res.data.row_count}`);
            } catch (_) { /* ignore parse errors */ }
          }
          if (tool.name === 'create_chart' && tool.result) {
            try {
              const res = typeof tool.result === 'string' ? JSON.parse(tool.result) : tool.result;
              const spec = res?.data?.vega_lite_spec || res?.vega_lite_spec || res;
              if (spec) contextLines.push(`Chart specification (truncated): ${JSON.stringify(spec).substring(0, 300)}`);
            } catch (_) { /* ignore parse errors */ }
          }
          if (tool.name === 'finish_task' && tool.result) {
            try {
              const res = typeof tool.result === 'string' ? JSON.parse(tool.result) : tool.result;
              if (res?.metadata?.sql_query && !contextLines.some(l => l.startsWith('SQL executed:'))) {
                contextLines.push(`SQL executed: ${res.metadata.sql_query}`);
              }
              if (res?.data?.columns && !contextLines.some(l => l.startsWith('Data columns:'))) {
                contextLines.push(`Data columns: ${JSON.stringify(res.data.columns)}`);
              }
              if (res?.data?.row_count != null && !contextLines.some(l => l.startsWith('Row count:'))) {
                contextLines.push(`Row count: ${res.data.row_count}`);
              }
            } catch (_) { /* ignore parse errors */ }
          }
        }

        if (contextLines.length > 0) {
          enrichedContent += '\n\n[Previous execution context]\n' + contextLines.join('\n');
        }
        return { role: msg.role, content: enrichedContent };
      }
      return { role: msg.role, content: msg.content };
    });

    // Track accumulated response outside of React state
    let accumulatedResponse = '';
    let accumulatedTools = [];
    let accumulatedMetrics = { inputTokens: 0, outputTokens: 0 };
    let accumulatedErrors = [];
    let accumulatedQaRecordId = null;
    let accumulatedRagSources = [];
    let accumulatedDataSummary = null;
    let accumulatedSanityWarnings = [];
    let accumulatedArtifacts = null; // null = use legacy single-table render; [] = text-only

    // Handle SSE messages
    const handleMessage = (data) => {
      if (data.type === 'content') {
        accumulatedResponse += (data.content || '');
        setCurrentResponse(accumulatedResponse);
      } else if (data.type === 'tool_use') {
        const newTool = {
          name: data.name,
          status: 'processing',
          input: data.input,
          agentMessage: data.agent_message || '',
          startTime: Date.now(),
          elapsedMs: data.elapsed_ms || null,
          inputTokens: data.input_tokens || null,
          outputTokens: data.output_tokens || null
        };
        accumulatedTools.push(newTool);
        setToolExecutions([...accumulatedTools]);
      } else if (data.type === 'tool_result') {
        if (accumulatedTools.length > 0) {
          const lastTool = accumulatedTools[accumulatedTools.length - 1];
          accumulatedTools[accumulatedTools.length - 1] = {
            ...lastTool,
            status: 'success',
            result: data.content,
            endTime: Date.now(),
            // Use client-side timing as fallback if backend didn't provide elapsed_ms
            elapsedMs: lastTool.elapsedMs || (lastTool.startTime ? Date.now() - lastTool.startTime : null)
          };
          setToolExecutions([...accumulatedTools]);
        }
      } else if (data.type === 'metrics') {
        accumulatedMetrics = {
          inputTokens: data.input_tokens || 0,
          outputTokens: data.output_tokens || 0
        };
        setStreamingMetrics(accumulatedMetrics);
      } else if (data.type === 'error') {
        console.error('Error:', data.error);
        accumulatedErrors.push(data.error);
        if (accumulatedTools.length > 0) {
          accumulatedTools[accumulatedTools.length - 1] = {
            ...accumulatedTools[accumulatedTools.length - 1],
            status: 'error',
            error: data.error
          };
          setToolExecutions([...accumulatedTools]);
        }
      } else if (data.type === 'dashboard_update') {
        // Agent wants to update a dashboard component
        if (onDashboardUpdate) {
          onDashboardUpdate(data.component_id, data.updates, data.component_title);
        }
      } else if (data.type === 'dashboard_add') {
        // Agent wants to add a new component to the dashboard
        if (onAddToDashboard) {
          onAddToDashboard(data.item);
          antMessage.success(`Added "${data.item.title || 'Component'}" to dashboard`);
        }
      } else if (data.type === 'dashboard_create') {
        // Agent wants to create a brand-new dashboard with a custom name
        if (onCreateDashboardByName) {
          onCreateDashboardByName(data.name);
        }
      } else if (data.type === 'dashboard_open') {
        // Agent wants to open an existing dashboard by name
        if (onOpenDashboardByName) {
          onOpenDashboardByName(data.name);
        }
      } else if (data.type === 'dashboard_filter_add') {
        // Agent wants to create a dashboard-level custom filter
        if (onAddDashboardFilter) {
          onAddDashboardFilter(data.filter);
          antMessage.success(`Added dashboard filter "${data.filter?.label || 'filter'}"`);
        }
      } else if (data.type === 'dashboard_filter_update') {
        // Agent wants to patch an existing dashboard-level custom filter
        if (onUpdateDashboardFilter) {
          onUpdateDashboardFilter(data.filter_id, data.patch || {});
          antMessage.success('Dashboard filter updated');
        }
      } else if (data.type === 'dashboard_add_tab') {
        // Agent wants to create a new dashboard tab
        if (onAddDashboardTab) {
          onAddDashboardTab(data.tab_name || '');
        }
      } else if (data.type === 'dashboard_rename_tab') {
        // Agent wants to rename a dashboard tab
        if (onRenameDashboardTab) {
          onRenameDashboardTab(data.old_name, data.new_name);
        }
      } else if (data.type === 'dashboard_set_default_tab') {
        // Agent wants to set/clear the dashboard default tab
        if (onSetDefaultDashboardTab) {
          onSetDefaultDashboardTab(data.tab_name || '');
        }
      } else if (data.type === 'chart_editor_edit') {
        // Agent wants to push a SQL/figure change into the open chart editor
        if (onChartEditorEdit) {
          onChartEditorEdit({ sql: data.sql, figure: data.figure, reason: data.reason });
        }
      } else if (data.type === 'qa_record') {
        // Backend has saved the Q&A record — store the id for feedback buttons
        accumulatedQaRecordId = data.qa_record_id;
      } else if (data.type === 'rag_sources') {
        // RAG gallery sources used in this response
        accumulatedRagSources = data.sources || [];
      } else if (data.type === 'data_summary') {
        // Phase 1: server-side per-column statistics (also embedded per-item in the artifacts list)
        accumulatedDataSummary = data.data_summary || null;
      } else if (data.type === 'sanity_warnings') {
        accumulatedSanityWarnings = data.warnings || [];
      } else if (data.type === 'delivery_items') {
        // Multi-artifact delivery: agent-picked list of tables/charts
        accumulatedArtifacts = Array.isArray(data.items) ? data.items : [];
      }
    };

    const handleError = (error) => {
      console.error('Stream error:', error);
      const errorMessage = accumulatedResponse 
        ? accumulatedResponse + '\n\n---\n\n**Error:** Connection lost. The response may be incomplete.'
        : `An error occurred while processing your request:\n\n${error?.message || String(error || 'Connection error. Please try again.')}`;
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: errorMessage,
        tools: accumulatedTools.length > 0 ? accumulatedTools : undefined,
        error: true 
      }]);
      setIsLoading(false);
      setCurrentResponse('');
      setToolExecutions([]);
    };

    const handleComplete = () => {
      const finalElapsedTime = processStartTimeRef.current
        ? Date.now() - processStartTimeRef.current
        : 0;

      // Build error content if no response was generated
      let finalContent = accumulatedResponse;
      let isError = false;
      if (!finalContent) {
        // Check for accumulated errors or tool errors
        const toolErrors = accumulatedTools
          .filter(t => t.status === 'error' && t.error)
          .map(t => `**${t.name}**: ${t.error}`);
        const allErrors = [...accumulatedErrors.map(e => String(e)), ...toolErrors];

        if (allErrors.length > 0) {
          finalContent = `An error occurred while processing your request:\n\n${allErrors.join('\n\n')}`;
          isError = true;
        } else if (accumulatedTools.length > 0) {
          // Agent completed work through tools but returned no text content —
          // synthesize a minimal response so the message (with its tool results,
          // metrics, and feedback buttons) is still persisted in the chat history.
          finalContent = 'The query was processed successfully. Check the Progress tab for details.';
        }
      }

      // Add final assistant message
      if (finalContent) {
        const assistantMessage = { 
          role: 'assistant', 
          content: finalContent,
          tools: accumulatedTools,
          error: isError,
          ragSources: accumulatedRagSources.length > 0 ? accumulatedRagSources : undefined,
          dataSummary: accumulatedDataSummary || undefined,
          sanityWarnings: accumulatedSanityWarnings.length > 0 ? accumulatedSanityWarnings : undefined,
          artifacts: accumulatedArtifacts, // null = legacy fallback, [] = text-only
          metrics: {
            processTimeMs: finalElapsedTime,
            processStartTime: processStartTimeRef.current,
            inputTokens: accumulatedMetrics.inputTokens,
            outputTokens: accumulatedMetrics.outputTokens
          },
          qaRecordId: accumulatedQaRecordId,
        };
        const messageIndex = messages.length + 1; // Next message index
        setMessages(prev => [...prev, assistantMessage]);
        
        // Initialize feedback state for this message
        if (accumulatedQaRecordId && !isError) {
          setMessageFeedback(prev => ({
            ...prev,
            [messageIndex]: { feedbackType: null, verified: false, qaRecordId: accumulatedQaRecordId }
          }));
        }
        
        // Auto-switch to Final Answer tab after response is complete
        setTimeout(() => {
          setMessageTabs(prev => ({ ...prev, [messageIndex]: 'answer' }));
        }, 100);
        
        // Save to Q&A history if callback provided (skip errors)
        if (onSaveQA && !isError) {
          onSaveQA(userMessage, finalContent)
            .catch(err => console.error('Failed to save Q&A:', err));
        }

        // Log interaction metrics if callback provided (skip errors)
        if (onLogInteraction && !isError) {
          onLogInteraction({
            user_message: userMessage,
            assistant_response: finalContent,
            input_tokens: accumulatedMetrics.inputTokens,
            output_tokens: accumulatedMetrics.outputTokens,
            response_time_ms: finalElapsedTime,
            tools_used: accumulatedTools.map(t => ({
              name: t.name,
              tool_type: 'ai_tool',
              input: t.input || null,
              output: t.result || null,
              elapsed_ms: t.elapsedMs || null,
              status: t.status === 'error' ? 'error' : 'success',
              error_message: t.error || null,
            })),
            tool_count: accumulatedTools.length,
            status: 'success',
          }).catch(err => console.error('Failed to log interaction:', err));
        }
      }
      setIsLoading(false);
      setCurrentResponse('');
      setToolExecutions([]);
      setStreamingTab('progress'); // Reset to progress for next message
      processStartTimeRef.current = null;
      setStreamingMetrics({ inputTokens: 0, outputTokens: 0 });
    };

    // Call the parent's send message handler with conversation history
    await onSendMessage(userMessage, conversationHistory, handleMessage, handleError, handleComplete);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`chat-container${isDashboardOpen ? ' dashboard-active' : ''}`}>
      {headerExtra && (
        <div className="chat-header-extra">
          {headerExtra}
        </div>
      )}
      <div className="messages-container">
        {messages.length === 0 && !isLoading ? (
          <div className="chat-welcome" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
              <div className="agent-title-chat" style={{ marginBottom: 16 }}>{agentTitle}</div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 0,
                }}
              >
                <div className="agent-icon-large" style={{ width: 92, height: 92, marginBottom: 0 }} title="AI Assistant">
                  <RobotOutlined style={{ fontSize: '56px', color: '#ffffff' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', flex: 1, justifyContent: 'center' }}>
            {/* <Text style={{ fontSize: '20px', color: 'var(--theme-primary)', fontWeight: 600, marginTop: 0, marginBottom: '8px', display: 'block' }}>
              Hello! 👋
            </Text> */}
            <Text style={{ fontSize: '15px', color: '#8c8c8c', display: 'block' }}>
              How can I help you?
            </Text>
            {isChatMode && (() => {
              const featureCards = [
                { icon: <img src="/slack.svg" alt="Slack" style={{ width: 32, height: 32 }} />,
                  title: 'Ask me anything in Slack', subtitle: 'Coming soon', disabled: true },
                { icon: <img src="/mcp.svg" alt="MCP" style={{ width: 32, height: 32 }} />,
                  title: 'Integrate me with your AI tools via CAI-MCP', subtitle: 'Coming soon', disabled: true },
                { icon: <ApiOutlined style={{ fontSize: 30, color: '#0958d9' }} />,
                  title: 'Ask me anything via API', subtitle: 'Coming soon', disabled: true },
                { icon: <ShareAltOutlined style={{ fontSize: 30, color: '#08979c' }} />,
                  title: 'Get my data via GraphAPI', subtitle: 'Open the GraphQL builder', disabled: false,
                  onClick: () => navigate('/data/graphql-builder') },
                { icon: <DashboardOutlined style={{ fontSize: 30, color: 'var(--theme-primary)' }} />,
                  title: 'Build a BI dashboard with me', subtitle: 'Open the list of dashboards', disabled: false,
                  onClick: () => navigate('/data/chat/dashboards?mode=hybrid') },
              ];
              return (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                    gap: 24,
                    width: '75vw',
                    // maxWidth: 1200,
                    marginTop: 24,
                    marginBottom: 8,
                  }}
                >
                  {featureCards.map((c, i) => (
                    <div
                      key={i}
                      onClick={c.disabled ? undefined : c.onClick}
                      style={{
                        background: 'var(--theme-cardBg)',
                        border: '1px solid var(--theme-cardBorder)',
                        borderRadius: 16,
                        padding: '18px 20px',
                        cursor: c.disabled ? 'not-allowed' : 'pointer',
                        opacity: c.disabled ? 0.7 : 1,
                        boxShadow: '0 2px 8px rgba(0,0,0,.06), 0 1px 3px rgba(0,0,0,.04)',
                        transition: 'box-shadow .15s, transform .15s, border-color .15s',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 14,
                        minHeight: 96,
                      }}
                      onMouseEnter={(e) => {
                        if (c.disabled) return;
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.12), 0 2px 6px rgba(0,0,0,.06)';
                        e.currentTarget.style.borderColor = 'var(--theme-primary)';
                        e.currentTarget.style.transform = 'translateY(-3px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.06), 0 1px 3px rgba(0,0,0,.04)';
                        e.currentTarget.style.borderColor = 'var(--theme-cardBorder)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, flexShrink: 0 }}>
                        {c.icon}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-textPrimary, #262626)', lineHeight: 1.35 }}>
                          {c.title}
                        </div>
                        <div style={{ fontSize: 12, color: c.disabled ? 'var(--theme-textSecondary, #8c8c8c)' : 'var(--theme-primary)' }}>
                          {c.subtitle}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
            {isChatMode && homepageTemplates.length > 0 && (() => {
              // Use publishedFolders for ordering; fall back to deriving from templates
              const orderedFolderNames = publishedFolders.length > 0
                ? publishedFolders.map(f => f.name).filter(name =>
                    homepageTemplates.some(t => t.folder === name && t.show_in_folder)
                  )
                : [...new Set(homepageTemplates.filter(t => t.folder && t.show_in_folder).map(t => t.folder))].sort();
              const standaloneTemplates = homepageTemplates.filter(t => !t.folder || !t.show_in_folder);

              const makeFolderPopover = (folder, prompts) => (
                <div style={{ minWidth: 280, maxWidth: 360 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, marginBottom: 4, borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {FOLDER_ICONS[folder] || '📁'} {folder}
                    </span>
                    <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setOpenFolderPopover(null)} />
                  </div>
                  {prompts.map((t, i) => (
                    <React.Fragment key={t.id}>
                      <div
                        style={{ padding: '9px 4px', cursor: 'pointer', fontSize: 13, color: PROMPT_COLORS[i % PROMPT_COLORS.length], transition: 'opacity .15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        onClick={() => { setInput(t.prompt); setOpenFolderPopover(null); antMessage.success('Prompt loaded — edit before sending if needed'); }}
                      >
                        {t.icon && <span style={{ marginRight: 6 }}>{t.icon}</span>}{t.label}
                      </div>
                      {i < prompts.length - 1 && <div style={{ height: 1, background: '#f0f0f0' }} />}
                    </React.Fragment>
                  ))}
                </div>
              );

              return (
                <>
                  <Text style={{ display: 'block', marginBottom: 24, fontSize: 13, color: '#8c8c8c', textAlign: 'center' }}>
                    Try these prompts
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', width: '75vw', margin: '0 auto' }}>
                  {orderedFolderNames.map(folder => {
                    const prompts = homepageTemplates.filter(t => t.folder === folder && t.show_in_folder);
                    return (
                      <Popover
                        key={folder}
                        content={makeFolderPopover(folder, prompts)}
                        trigger="click"
                        open={openFolderPopover === folder}
                        onOpenChange={open => setOpenFolderPopover(open ? folder : null)}
                        placement="bottom"
                        arrow={false}
                        zIndex={1200}
                        overlayInnerStyle={{ padding: '12px 16px', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,.12)' }}
                      >
                        <Button
                          style={{ borderRadius: 20, fontSize: 13, paddingLeft: 14, paddingRight: 14 }}
                          type={openFolderPopover === folder ? 'primary' : 'default'}
                        >
                          {FOLDER_ICONS[folder] || '📁'} {folder} <DownOutlined style={{ fontSize: 10, marginLeft: 2 }} />
                        </Button>
                      </Popover>
                    );
                  })}
                  {standaloneTemplates.length > 0 && standaloneTemplates.slice(0, 6).map(t => (
                    <Button
                      key={t.id}
                      style={{ borderRadius: 20, fontSize: 13 }}
                      onClick={() => { setInput(t.prompt); antMessage.success('Prompt loaded — edit before sending if needed'); }}
                    >
                      {t.icon && <span style={{ marginRight: 4 }}>{t.icon}</span>}{t.label}
                    </Button>
                  ))}
                  </div>
                </>
              );
            })()}
            {isAdmin && (
              <Button
                type="link"
                icon={<BulbOutlined />}
                onClick={() => setPromptLibraryVisible(true)}
                style={{ marginTop: 16, fontSize: 13, color: '#8c8c8c' }}
              >
                Manage prompts in the Prompt Library
              </Button>
            )}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                {message.role === 'user' ? (
                  <>
                    <div className="message-header">
                      <span className="message-role">You</span>
                    </div>
                    <div className="message-content">
                      {/* XSS-safe: no rehypeRaw so raw HTML in message.content is escaped (TC-DC-EC-10). */}
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="message-header">
                      <span className="message-role">Assistant</span>
                    </div>
                    <Tabs
                      activeKey={messageTabs[index] || 'progress'}
                      onChange={(key) => setMessageTabs(prev => ({ ...prev, [index]: key }))}
                      items={[
                        {
                          key: 'progress',
                          label: 'Progress',
                          children: (
                            <div className="tab-content">
                              {/* Process metrics summary */}
                              {message.metrics && (message.metrics.processTimeMs > 0 || message.metrics.inputTokens > 0) && (
                                <div style={{ 
                                  display: 'flex', gap: '16px', marginBottom: '12px', padding: '8px 12px', 
                                  background: 'var(--theme-cardBgMuted)', 
                                  borderRadius: '6px', fontSize: '0.85em', color: 'var(--theme-textSecondary, #595959)',
                                  border: '1px solid var(--theme-cardBorder)', alignItems: 'center', flexWrap: 'wrap'
                                }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <ClockCircleOutlined style={{ color: 'var(--theme-primary)' }} />
                                    <strong>{(message.metrics.processTimeMs / 1000).toFixed(1)}s</strong> total
                                  </span>
                                  {message.tools && message.tools.length > 0 && (() => {
                                    const totalToolMs = message.tools.reduce((sum, t) => sum + (t.elapsedMs || 0), 0);
                                    const thinkingMs = Math.max(0, message.metrics.processTimeMs - totalToolMs);
                                    return (
                                      <>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8c8c8c' }}>
                                          🧠 <strong style={{ color: '#595959' }}>{(thinkingMs / 1000).toFixed(1)}s</strong> thinking
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8c8c8c' }}>
                                          🔧 <strong style={{ color: '#595959' }}>{(totalToolMs / 1000).toFixed(1)}s</strong> tools
                                        </span>
                                      </>
                                    );
                                  })()}
                                  {(message.metrics.inputTokens > 0 || message.metrics.outputTokens > 0) && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                      📊 <strong>{(message.metrics.inputTokens + message.metrics.outputTokens).toLocaleString()}</strong> tokens
                                      <span style={{ color: '#8c8c8c', fontSize: '0.9em' }}>
                                        ({message.metrics.inputTokens.toLocaleString()} in / {message.metrics.outputTokens.toLocaleString()} out)
                                      </span>
                                    </span>
                                  )}
                                </div>
                              )}
                              {message.tools && message.tools.length > 0 ? (
                                message.tools.map((tool, idx) => {
                                  // Calculate per-step thinking time
                                  const prevToolEnd = idx > 0 ? (message.tools[idx - 1].endTime || message.tools[idx - 1].startTime + (message.tools[idx - 1].elapsedMs || 0)) : null;
                                  const thinkingMs = tool.startTime ? (prevToolEnd ? tool.startTime - prevToolEnd : (message.metrics?.processStartTime ? tool.startTime - message.metrics.processStartTime : null)) : null;
                                  return (
                                  <div key={idx} className="tool-execution" style={{ marginBottom: '12px', padding: '10px 12px', background: 'var(--theme-cardBgMuted)', borderRadius: '4px', borderLeft: '3px solid var(--theme-primary)' }}>
                                    {tool.agentMessage && (
                                      <div style={{ marginBottom: '8px', padding: '7px 10px', background: '#f5f0e8', borderRadius: '4px', borderLeft: '3px solid #e8c870', color: '#595959', fontSize: '0.95em' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                          <span style={{ fontSize: '13px', color: '#b8860b', fontWeight: 600 }}>🧠 Agent reasoning</span>
                                          {thinkingMs != null && thinkingMs > 0 && (
                                            <span style={{ fontSize: '13px', color: '#8c8c8c' }}>🧠 {(thinkingMs / 1000).toFixed(1)}s thinking</span>
                                          )}
                                        </div>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{tool.agentMessage}</ReactMarkdown>
                                      </div>
                                    )}
                                    <div className="tool-name" style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                      <span>🔧 <strong>{tool.name}</strong></span>
                                      <span 
                                        className={`tool-status tool-status-${
                                          tool.status === 'success' ? 'success' : 
                                          tool.status === 'error' ? 'error' : 
                                          'processing'
                                        }`}
                                      >
                                        {tool.status}
                                      </span>
                                      {(tool.elapsedMs || tool.inputTokens > 0) && (
                                        <span style={{ fontSize: '0.8em', color: '#8c8c8c', marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                          {tool.elapsedMs != null && (
                                            <span>⏱ {(tool.elapsedMs / 1000).toFixed(1)}s</span>
                                          )}
                                          {(tool.inputTokens > 0 || tool.outputTokens > 0) && (
                                            <span>🔤 {((tool.inputTokens || 0) + (tool.outputTokens || 0)).toLocaleString()} tok</span>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    {(tool.input || tool.result) && (
                                      <Collapse
                                        size="small"
                                        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} style={{ color: '#8c8c8c' }} />}
                                        style={{ background: 'transparent', border: 'none' }}
                                        items={[
                                          ...(tool.input ? [{
                                            key: 'input',
                                            label: <span style={{ fontSize: '0.83em', fontWeight: 600, color: '#666' }}>📥 Input</span>,
                                            children: (
                                              <div className="tool-details" style={{ fontSize: '0.83em', color: '#666', background: '#fff', padding: '7px', borderRadius: '4px', maxHeight: '150px', overflow: 'auto' }}>
                                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(tool.input, null, 2)}</pre>
                                              </div>
                                            ),
                                            style: { border: 'none', padding: 0 }
                                          }] : []),
                                          ...(tool.result ? [{
                                            key: 'output',
                                            label: <span style={{ fontSize: '0.83em', fontWeight: 600, color: '#666' }}>📤 Output</span>,
                                            children: (
                                              <div className="tool-details" style={{ fontSize: '0.83em', color: '#666', background: '#fff', padding: '7px', borderRadius: '4px', maxHeight: '200px', overflow: 'auto' }}>
                                                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{typeof tool.result === 'string' ? tool.result : JSON.stringify(tool.result, null, 2)}</pre>
                                              </div>
                                            ),
                                            style: { border: 'none', padding: 0 }
                                          }] : [])
                                        ]}
                                      />
                                    )}
                                  </div>
                                  );
                                })
                              ) : (
                                <div style={{ color: '#8c8c8c' }}>No progress information available</div>
                              )}
                            </div>
                          )
                        },
                        {
                          key: 'artifacts',
                          label: 'Artifacts',
                          children: (
                            <div className="tab-content">
                              {(() => {
                                // Aggregate deliverables from all tools
                                const deliverables = {
                                  sql: null,
                                  data: null,
                                  chart: null,
                                  confidence: null,
                                  confidenceBreakdown: null,
                                  ontologyMermaid: null,
                                  sources: []
                                };

                                message.tools?.forEach(tool => {
                                  if (tool.result) {
                                    const result = tool.result;
                                    
                                    // Extract SQL from multiple possible paths
                                    if (result.sql) deliverables.sql = result.sql;
                                    if (result.metadata?.sql_query) deliverables.sql = result.metadata.sql_query;
                                    if (result.metadata?.sql) deliverables.sql = result.metadata.sql;
                                    if (result.data?.sql) deliverables.sql = result.data.sql;
                                    // fetch_data: use tool.input.sql as the most direct source
                                    if ((tool.name === 'fetch_data') && tool.input?.sql) deliverables.sql = tool.input.sql;
                                    
                                    // Extract data/table
                                    if (result.data) {
                                      if (result.data.columns && result.data.rows) {
                                        deliverables.data = result.data;
                                      } else if (result.data.data && result.data.data.columns) {
                                        deliverables.data = result.data.data;
                                      }
                                    }
                                    
                                    // Extract chart — only accept objects that have actual spec keys
                                    // (empty {} or [] from finish_task must not overwrite a valid spec)
                                    const isValidChart = (c) => c && typeof c === 'object' && !Array.isArray(c) && Object.keys(c).length > 0;
                                    if (isValidChart(result.chart)) deliverables.chart = result.chart;
                                    if (isValidChart(result.vega_spec)) deliverables.chart = result.vega_spec;
                                    if (isValidChart(result.vega_lite_spec)) deliverables.chart = result.vega_lite_spec;
                                    if (isValidChart(result.data?.vega_lite_spec)) deliverables.chart = result.data.vega_lite_spec;
                                    if (isValidChart(result.data?.chart)) deliverables.chart = result.data.chart;
                                    if (isValidChart(result.metadata?.chart)) deliverables.chart = result.metadata.chart;
                                    // Plotly figures (post chart-system migration to Plotly)
                                    if (isValidChart(result.plotly_figure)) deliverables.chart = result.plotly_figure;
                                    if (isValidChart(result.data?.plotly_figure)) deliverables.chart = result.data.plotly_figure;
                                    if (isValidChart(result.metadata?.plotly_figure)) deliverables.chart = result.metadata.plotly_figure;
                                    // Also accept JSON string specs
                                    if (typeof result.chart === 'string' && result.chart.length > 2) deliverables.chart = result.chart;
                                    if (typeof result.data?.chart === 'string' && result.data.chart.length > 2) deliverables.chart = result.data.chart;

                                    // Extract ontology mermaid diagram
                                    // From manager-level delivery (assign_task_to_data_agent / answer tool results)
                                    if (result.delivery?.data?.ontology_mermaid) {
                                      deliverables.ontologyMermaid = result.delivery.data.ontology_mermaid;
                                    }
                                    // Direct data path (when tool result contains data.ontology_mermaid)
                                    if (result.data?.ontology_mermaid) {
                                      deliverables.ontologyMermaid = result.data.ontology_mermaid;
                                    }

                                    // Extract confidence and breakdown
                                    if (result.confidence) deliverables.confidence = result.confidence;
                                    if (result.metadata?.confidence) deliverables.confidence = result.metadata.confidence;
                                    if (result.metadata?.confidence_breakdown) {
                                      deliverables.confidenceBreakdown = result.metadata.confidence_breakdown;
                                    }
                                    
                                    // Extract sources (for knowledge base)
                                    if (tool.name?.includes('search') || tool.name?.includes('knowledge')) {
                                      if (typeof result === 'string') {
                                        deliverables.sources.push(result);
                                      } else if (result.sources) {
                                        deliverables.sources.push(...result.sources);
                                      } else if (result.results) {
                                        deliverables.sources.push(...result.results);
                                      }
                                    }
                                  }
                                });

                                const hasDeliverables = deliverables.sql || deliverables.data || deliverables.chart || 
                                                       deliverables.confidence || deliverables.ontologyMermaid ||
                                                       deliverables.sources.length > 0;

                                if (!hasDeliverables) {
                                  return <div style={{ color: '#8c8c8c' }}>No artifacts available</div>;
                                }

                                return (
                                  <div>
                                    {deliverables.ontologyMermaid && (
                                      <div className="chat-artifact-card">
                                        <div className="chat-artifact-header">
                                          <div className="chat-artifact-title">
                                            <FileSearchOutlined />
                                            <span className="chat-artifact-title-text">Semantic Relationship Diagram</span>
                                          </div>
                                        </div>
                                        <div className="chat-artifact-body">
                                          <MermaidViewer code={deliverables.ontologyMermaid} height={400} />
                                        </div>
                                      </div>
                                    )}

                                    {/* Standalone SQL block removed: SQL is now an attachment-button on each table/chart. */}
                                    {(() => {
                                      const dlvHasStats = !!(message.dataSummary || (message.sanityWarnings && message.sanityWarnings.length > 0));
                                      return null && dlvHasStats; // helper closure only — actual blocks below
                                    })()}

                                    {deliverables.data && (
                                      <div className="chat-artifact-card">
                                        <div className="chat-artifact-header">
                                          <div className="chat-artifact-title">
                                            <TableOutlined />
                                            <span className="chat-artifact-title-text">Data Table</span>
                                          </div>
                                          <div className="chat-artifact-actions">
                                            {deliverables.sql && renderAddToDashboardBtn({
                                              item_type: 'table',
                                              title: 'Data Table',
                                              sql_query: deliverables.sql,
                                            }, { iconOnly: true })}
                                            <Tooltip title="Full Screen">
                                              <Button type="text" size="small" icon={<FullscreenOutlined />}
                                                onClick={() => { setFullscreenChartReady(false); setFullscreenModal({ visible: true, type: 'table', content: deliverables.data, title: 'Data Table' }); }} />
                                            </Tooltip>
                                            <Dropdown
                                              trigger={['click']}
                                              menu={{ items: [
                                                {
                                                  key: 'sql',
                                                  icon: <CodeOutlined />,
                                                  label: 'View SQL',
                                                  disabled: !deliverables.sql,
                                                  onClick: () => setSqlModal({ visible: true, sql: deliverables.sql, title: 'Data Table — SQL' }),
                                                },
                                              ] }}
                                            >
                                              <Tooltip title="More">
                                                <Button type="text" size="small" icon={<MoreOutlined />} />
                                              </Tooltip>
                                            </Dropdown>
                                          </div>
                                        </div>
                                        <div className="chat-artifact-body">
                                          {deliverables.data.columns && deliverables.data.rows ? (
                                            <DataTableWithWhereFilter
                                              initialData={{ columns: deliverables.data.columns, rows: deliverables.data.rows, row_count: deliverables.data.row_count, truncated: deliverables.data.truncated }}
                                              sqlQuery={deliverables.sql}
                                              dataSummary={message.dataSummary}
                                              maxRows={200}
                                              filterValues={messageFilterValues[index]}
                                              onFilterValuesChange={(vals) => setMessageFilterValues(prev => ({ ...prev, [index]: vals }))}
                                            />
                                          ) : (
                                            <div style={{ fontSize: '0.85em', color: 'var(--theme-textSecondary)', overflow: 'auto', maxHeight: '400px' }}>
                                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(deliverables.data, null, 2)}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {deliverables.chart && (
                                      <div className="chat-artifact-card">
                                        <div className="chat-artifact-header">
                                          <div className="chat-artifact-title">
                                            <BarChartOutlined />
                                            <span className="chat-artifact-title-text">Visualization</span>
                                          </div>
                                          <div className="chat-artifact-actions">
                                            {deliverables.sql && (() => {
                                              const specForSave = typeof deliverables.chart === 'string'
                                                ? JSON.parse(deliverables.chart)
                                                : { ...deliverables.chart };
                                              // For Plotly figures, `data` IS the traces array — keep it.
                                              // For Vega-Lite specs, `data` is inline values ({values: [...]})
                                              // and the dashboard re-fetches via SQL, so strip it.
                                              if (!Array.isArray(specForSave.data)) delete specForSave.data;
                                              return renderAddToDashboardBtn({
                                                item_type: 'chart',
                                                title: specForSave.title || 'Visualization',
                                                sql_query: deliverables.sql,
                                                chart_config: specForSave,
                                                grid_w: 6,
                                                grid_h: 6,
                                              }, { iconOnly: true });
                                            })()}
                                            <Tooltip title="Full Screen">
                                              <Button type="text" size="small" icon={<FullscreenOutlined />}
                                                onClick={() => { setFullscreenChartReady(false); setFullscreenModal({ visible: true, type: 'chart', content: deliverables.chart, title: 'Visualization' }); }} />
                                            </Tooltip>
                                            <Dropdown
                                              trigger={['click']}
                                              menu={{ items: [
                                                {
                                                  key: 'table',
                                                  icon: <DatabaseOutlined />,
                                                  label: 'View underlying data',
                                                  disabled: !deliverables.data,
                                                  onClick: () => setTableInChartModal({ visible: true, table: deliverables.data, title: 'Visualization — Underlying data' }),
                                                },
                                                {
                                                  key: 'stats',
                                                  icon: <FundOutlined />,
                                                  label: 'Quick stats',
                                                  disabled: !(message.dataSummary || (message.sanityWarnings && message.sanityWarnings.length > 0)),
                                                  onClick: () => setStatsModal({ visible: true, dataSummary: message.dataSummary, sanityWarnings: message.sanityWarnings, title: 'Visualization — Quick Stats' }),
                                                },
                                                {
                                                  key: 'sql',
                                                  icon: <CodeOutlined />,
                                                  label: 'View SQL',
                                                  disabled: !deliverables.sql,
                                                  onClick: () => setSqlModal({ visible: true, sql: deliverables.sql, title: 'Visualization — SQL' }),
                                                },
                                                {
                                                  key: 'json',
                                                  icon: <CodeOutlined />,
                                                  label: 'View chart JSON',
                                                  onClick: () => setChartJsonModal({ visible: true, spec: deliverables.chart }),
                                                },
                                              ] }}
                                            >
                                              <Tooltip title="More">
                                                <Button type="text" size="small" icon={<MoreOutlined />} />
                                              </Tooltip>
                                            </Dropdown>
                                          </div>
                                        </div>
                                        <div className="chat-artifact-body">
                                          {isPlotlyFigure(deliverables.chart)
                                            ? <FilterablePlotly chartSpec={deliverables.chart} tableData={deliverables.data} />
                                            : (deliverables.sql
                                              ? <DeliveryChartWithFilters chartSpec={deliverables.chart} initialData={deliverables.data} sqlQuery={deliverables.sql}
                                                  filterValues={messageFilterValues[index]} onFilterValuesChange={(vals) => setMessageFilterValues(prev => ({...prev, [index]: vals}))} />
                                              : <FilterableChart chartSpec={deliverables.chart} tableData={deliverables.data} />)}
                                        </div>
                                      </div>
                                    )}

                                    {deliverables.confidence && (() => {
                                      const confObj = typeof deliverables.confidence === 'object' ? deliverables.confidence : null;
                                      const bd = deliverables.confidenceBreakdown || confObj;
                                      const pct = typeof deliverables.confidence === 'number'
                                        ? deliverables.confidence * 100
                                        : (confObj?.score ? confObj.score * 100 : 0);
                                      const level = bd?.level || '';
                                      const gaugeColor = pct >= 85 ? '#52c41a' : pct >= 70 ? '#faad14' : '#ff4d4f';
                                      const breakdown = bd?.breakdown || {};
                                      const summaryText = bd?.summary || '';
                                      const getBarColor = (score) => score >= 8 ? '#52c41a' : score >= 6 ? '#faad14' : '#ff4d4f';
                                      const factorMeta = {
                                        requirement_clarity: { icon: <FileSearchOutlined />, label: 'Requirement Clarity' },
                                        query_reliability: { icon: <DatabaseOutlined />, label: 'Query Reliability' },
                                        data_fitness: { icon: <FundOutlined />, label: 'Data Fitness' },
                                      };
                                      return (
                                        <div style={{ marginBottom: '16px', padding: '16px', background: 'var(--theme-cardBgMuted)', borderRadius: '8px', border: '1px solid var(--theme-cardBorder)' }}>
                                          {/* Header: score + level + verified */}
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px' }}>
                                            <div style={{
                                              width: 56, height: 56, borderRadius: '50%',
                                              background: `conic-gradient(${gaugeColor} ${pct * 3.6}deg, #f0f0f0 0deg)`,
                                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                              <div style={{
                                                width: 46, height: 46, borderRadius: '50%', background: 'var(--theme-cardBgMuted)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              }}>
                                                <span style={{ fontSize: '1.15em', fontWeight: 700, color: gaugeColor }}>{Math.round(pct)}%</span>
                                              </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <span style={{ fontWeight: 700, fontSize: '1em', color: '#262626' }}>
                                                  Confidence: <span style={{ color: gaugeColor, textTransform: 'uppercase' }}>{level}</span>
                                                </span>
                                                {bd?.verified_by_admin && (
                                                  <Tooltip title={`Near-exact match to admin-verified Q&A: "${bd.verified_match_label}" (${Math.round((bd.verified_match_similarity || 0) * 100)}% similarity)`}>
                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '1px 8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                                                      <SafetyCertificateOutlined style={{ color: '#52c41a', fontSize: '0.85em' }} />
                                                      <span style={{ fontSize: '0.72em', fontWeight: 600, color: '#389e0d' }}>Verified</span>
                                                    </div>
                                                  </Tooltip>
                                                )}
                                              </div>
                                              {summaryText && (
                                                <div style={{ lineHeight: '1.4' }}>
                                                  <span style={{ fontWeight: 600, color: '#434343' }}>Reason: </span>
                                                  <span style={{ color: '#595959' }}>{summaryText}</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          {/* Divider */}
                                          <div style={{ height: 1, background: '#e8e8e8', margin: '14px 0' }} />

                                          {/* Factor breakdown — expanded */}
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {Object.entries(breakdown).map(([factor, details]) => {
                                              const score = details.score || 0;
                                              const pctBar = score * 10;
                                              const meta = factorMeta[factor] || { icon: null, label: factor.replace(/_/g, ' ') };
                                              const weightPct = Math.round((details.weight || 0) * 100);
                                              return (
                                                <div key={factor}>
                                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500, fontSize: '0.82em', color: '#434343' }}>
                                                      <span style={{ color: getBarColor(score), fontSize: '0.95em' }}>{meta.icon}</span>
                                                      {meta.label}
                                                      <span style={{ fontSize: '0.85em', color: '#bfbfbf', fontWeight: 400 }}>({weightPct}%)</span>
                                                    </span>
                                                    <span style={{ fontSize: '0.75em', color: '#8c8c8c', fontWeight: 600 }}>{score}/10</span>
                                                  </div>
                                                  <Progress percent={pctBar} size="small" strokeColor={getBarColor(score)} showInfo={false} style={{ marginBottom: '2px' }} />
                                                  {details.reason && (
                                                    <div style={{ fontSize: '0.75em', color: '#8c8c8c', lineHeight: '1.4', marginTop: '2px' }}>{details.reason}</div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {bd?.gallery_boost > 0 && !bd?.verified_by_admin && (
                                            <div style={{ marginTop: '10px', fontSize: '0.75em', color: '#8c8c8c' }}>
                                              +{(bd.gallery_boost * 100).toFixed(1)}% confidence boost from {bd.gallery_match_count > 1 ? `${bd.gallery_match_count} verified answers` : 'verified answer'} (best: {Math.round((bd.gallery_best_similarity || 0) * 100)}% match)
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {deliverables.sources.length > 0 && (
                                      <div className="chat-artifact-card">
                                        <div className="chat-artifact-header">
                                          <div className="chat-artifact-title">
                                            <FileSearchOutlined />
                                            <span className="chat-artifact-title-text">Knowledge Sources</span>
                                          </div>
                                        </div>
                                        <div className="chat-artifact-body" style={{ fontSize: '0.9em', color: 'var(--theme-textSecondary)' }}>
                                          {deliverables.sources.map((source, idx) => (
                                            <div key={idx} style={{ marginBottom: '8px', padding: '8px', background: 'var(--theme-backgroundSecondary)', borderRadius: '4px' }}>
                                              {typeof source === 'string' ? source : JSON.stringify(source, null, 2)}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )
                        },
                        {
                          key: 'answer',
                          label: 'Final Answer',
                          children: (
                            <div className="tab-content">
                              {/* Confidence indicator */}
                              {(() => {
                                let conf = null;
                                let confBreakdown = null;
                                message.tools?.forEach(tool => {
                                  if (tool.result) {
                                    const r = tool.result;
                                    if (r.confidence) conf = r.confidence;
                                    if (r.metadata?.confidence) conf = r.metadata.confidence;
                                    if (r.metadata?.confidence_breakdown) confBreakdown = r.metadata.confidence_breakdown;
                                  }
                                });
                                if (!conf) return null;
                                const confObj = typeof conf === 'object' ? conf : null;
                                const bd = confBreakdown || confObj;
                                const pct = typeof conf === 'number' ? conf * 100 : (confObj?.score ? confObj.score * 100 : 0);
                                const gaugeColor = pct >= 85 ? '#52c41a' : pct >= 70 ? '#faad14' : '#ff4d4f';
                                const summaryText = bd?.summary || '';
                                return (
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    {bd?.verified_by_admin && (
                                      <Tooltip title={`Near-exact match to admin-verified Q&A: "${bd.verified_match_label}" (${Math.round((bd.verified_match_similarity || 0) * 100)}% similarity)`}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                                          <SafetyCertificateOutlined style={{ color: '#52c41a', fontSize: '0.9em' }} />
                                          <span style={{ fontSize: '0.75em', fontWeight: 600, color: '#389e0d' }}>Verified</span>
                                        </div>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={summaryText || 'Click to view confidence details'}>
                                      <div
                                        onClick={() => setConfidenceModal({ visible: true, confidence: conf, breakdown: bd })}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                                          padding: '5px 14px', borderRadius: '8px',
                                          background: gaugeColor === '#52c41a' ? '#f6ffed' : gaugeColor === '#faad14' ? '#fffbe6' : '#fff2f0',
                                          border: `1px solid ${gaugeColor}40`,
                                          transition: 'box-shadow 0.2s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.boxShadow = `0 0 0 2px ${gaugeColor}30`}
                                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                                      >
                                        <SafetyCertificateOutlined style={{ color: gaugeColor, fontSize: '1.4em' }} />
                                        <span style={{ fontWeight: 600, fontSize: '0.85em', color: gaugeColor }}>{Math.round(pct)}% confident about this answer</span>
                                      </div>
                                    </Tooltip>
                                  </div>
                                );
                              })()}
                              {message.error && (
                                <div style={{ 
                                  display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '10px 14px',
                                  background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px', color: '#cf1322'
                                }}>
                                  <WarningOutlined style={{ fontSize: '16px' }} />
                                  <strong>Error occurred during processing</strong>
                                </div>
                              )}
                              {/* XSS-safe: no rehypeRaw (TC-DC-EC-10). */}
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {message.content}
                              </ReactMarkdown>

                              {/* Sanity warnings stay inline as alerts (deserve visibility).
                                  Quick stats moved into a per-table button on each artifact. */}
                              {message.sanityWarnings && message.sanityWarnings.length > 0 && (
                                <QuickStatsPanel
                                  dataSummary={null}
                                  sanityWarnings={message.sanityWarnings}
                                />
                              )}

                              {/* RAG source attribution */}
                              {message.ragSources?.length > 0 && (
                                <div style={{
                                  marginTop: '12px',
                                  padding: '8px 12px',
                                  background: '#f6ffed',
                                  border: '1px solid #b7eb8f',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  color: '#389e0d',
                                }}>
                                  <span style={{ fontWeight: 600 }}>📚 Sources from Data Gallery: </span>
                                  {message.ragSources.map((src, i) => (
                                    <span key={i}>
                                      {i > 0 && ' · '}
                                      {src.source_type === 'verified_answer' && (
                                        <span>✅ Verified: <em>{src.label?.length > 60 ? src.label.substring(0, 60) + '…' : src.label}</em></span>
                                      )}
                                      {src.source_type === 'dashboard_component' && (
                                        <span>📊 Dashboard: <em>{src.label}</em>{src.dashboard ? ` (${src.dashboard})` : ''}</span>
                                      )}
                                      {src.source_type !== 'verified_answer' && src.source_type !== 'dashboard_component' && (
                                        <span>📝 {src.label}</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* Show data table(s) and chart(s) in Final Answer tab.
                                  Source of truth:
                                    - message.artifacts != null  → render those items in order (agent-picked)
                                    - message.artifacts == null  → legacy fallback: extract last table + last chart from tools
                                    - message.artifacts == []    → text-only answer, render nothing here */}
                              {(() => {
                                // Helper that renders one delivery slot (table or chart) using
                                // the same Filter/Fullscreen/Add-to-dashboard chrome as before.
                                const renderTableSlot = ({ table, sql, title, key }) => {
                                  return (
                                    <div key={key} className="chat-artifact-card">
                                      <div className="chat-artifact-header">
                                        <div className="chat-artifact-title">
                                          <TableOutlined />
                                          <span className="chat-artifact-title-text">{title || 'Data Table'}</span>
                                        </div>
                                        <div className="chat-artifact-actions">
                                          {sql && renderAddToDashboardBtn({
                                            item_type: 'table',
                                            title: title || 'Data Table',
                                            sql_query: sql,
                                          }, { iconOnly: true })}
                                          <Tooltip title="Full Screen">
                                            <Button type="text" size="small" icon={<FullscreenOutlined />}
                                              onClick={() => { setFullscreenChartReady(false); setFullscreenModal({ visible: true, type: 'table', content: { ...table, dataSummary: table.data_summary }, title: title || 'Data Table' }); }} />
                                          </Tooltip>
                                          <Dropdown
                                            trigger={['click']}
                                            menu={{ items: [
                                              {
                                                key: 'sql',
                                                icon: <CodeOutlined />,
                                                label: 'View SQL',
                                                disabled: !sql,
                                                onClick: () => setSqlModal({ visible: true, sql, title: (title || 'Data Table') + ' — SQL' }),
                                              },
                                            ] }}
                                          >
                                            <Tooltip title="More">
                                              <Button type="text" size="small" icon={<MoreOutlined />} />
                                            </Tooltip>
                                          </Dropdown>
                                        </div>
                                      </div>
                                      <div className="chat-artifact-body">
                                        {table.columns && table.rows ? (
                                          <DataTableWithWhereFilter
                                            initialData={{ columns: table.columns, rows: table.rows, row_count: table.row_count, truncated: table.truncated }}
                                            sqlQuery={sql}
                                            dataSummary={table.data_summary}
                                            maxRows={200}
                                            filterValues={messageFilterValues[`${index}-${key}`]}
                                            onFilterValuesChange={(vals) => setMessageFilterValues(prev => ({ ...prev, [`${index}-${key}`]: vals }))}
                                          />
                                        ) : (
                                          <div style={{ fontSize: '0.85em', color: 'var(--theme-textSecondary)', overflow: 'auto', maxHeight: '400px' }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(table, null, 2)}</pre>
                                          </div>
                                        )}
                                        {/* Sanity warnings stay inline as alert banners */}
                                        {table.sanity_warnings && table.sanity_warnings.length > 0 && (
                                          <QuickStatsPanel dataSummary={null} sanityWarnings={table.sanity_warnings} />
                                        )}
                                      </div>
                                    </div>
                                  );
                                };

                                const renderChartSlot = ({ spec, sql, table, title, key }) => {
                                  const hasTable = !!(table && table.columns && table.rows && table.rows.length);
                                  const hasStats = !!(table && (table.data_summary || (table.sanity_warnings && table.sanity_warnings.length > 0)));
                                  return (
                                    <div key={key} className="chat-artifact-card">
                                      <div className="chat-artifact-header">
                                        <div className="chat-artifact-title">
                                          <BarChartOutlined />
                                          <span className="chat-artifact-title-text">{title || (spec?.title) || 'Visualization'}</span>
                                        </div>
                                        <div className="chat-artifact-actions">
                                          {sql && (() => {
                                            const specForSave = typeof spec === 'string' ? JSON.parse(spec) : { ...spec };
                                            // Preserve Plotly traces (`data` array); strip Vega-Lite inline data.
                                            if (!Array.isArray(specForSave.data)) delete specForSave.data;
                                            return renderAddToDashboardBtn({
                                              item_type: 'chart',
                                              title: title || specForSave.title || 'Visualization',
                                              sql_query: sql,
                                              chart_config: specForSave,
                                              grid_w: 6,
                                              grid_h: 6,
                                            }, { iconOnly: true });
                                          })()}
                                          <Tooltip title="Full Screen">
                                            <Button type="text" size="small" icon={<FullscreenOutlined />}
                                              onClick={() => { setFullscreenChartReady(false); setFullscreenModal({ visible: true, type: 'chart', content: spec, title: title || 'Visualization' }); }} />
                                          </Tooltip>
                                          <Dropdown
                                            trigger={['click']}
                                            menu={{ items: [
                                              {
                                                key: 'table',
                                                icon: <DatabaseOutlined />,
                                                label: 'View underlying data',
                                                disabled: !hasTable,
                                                onClick: () => setTableInChartModal({ visible: true, table, title: (title || 'Chart') + ' — Underlying data' }),
                                              },
                                              {
                                                key: 'stats',
                                                icon: <FundOutlined />,
                                                label: 'Quick stats',
                                                disabled: !hasStats,
                                                onClick: () => setStatsModal({ visible: true, dataSummary: table?.data_summary, sanityWarnings: table?.sanity_warnings, title: (title || 'Chart') + ' — Quick Stats' }),
                                              },
                                              {
                                                key: 'sql',
                                                icon: <CodeOutlined />,
                                                label: 'View SQL',
                                                disabled: !sql,
                                                onClick: () => setSqlModal({ visible: true, sql, title: (title || 'Chart') + ' — SQL' }),
                                              },
                                              {
                                                key: 'json',
                                                icon: <CodeOutlined />,
                                                label: 'View chart JSON',
                                                onClick: () => setChartJsonModal({ visible: true, spec }),
                                              },
                                            ] }}
                                          >
                                            <Tooltip title="More">
                                              <Button type="text" size="small" icon={<MoreOutlined />} />
                                            </Tooltip>
                                          </Dropdown>
                                        </div>
                                      </div>
                                      <div className="chat-artifact-body">
                                        {isPlotlyFigure(spec)
                                          ? <FilterablePlotly chartSpec={spec} tableData={table} />
                                          : (sql
                                            ? <DeliveryChartWithFilters chartSpec={spec} initialData={table} sqlQuery={sql}
                                                filterValues={messageFilterValues[index + ':' + key]} onFilterValuesChange={(vals) => setMessageFilterValues(prev => ({...prev, [index + ':' + key]: vals}))} />
                                            : <FilterableChart chartSpec={spec} tableData={table} />)}
                                      </div>
                                    </div>
                                  );
                                };

                                const renderJsonSlot = ({ jsonData, sql, title, itemCount, key }) => {
                                  const jsonText = (() => {
                                    try { return JSON.stringify(jsonData, null, 2); } catch { return ''; }
                                  })();
                                  return (
                                    <div key={key} className="chat-artifact-card">
                                      <div className="chat-artifact-header">
                                        <div className="chat-artifact-title">
                                          <CodeOutlined />
                                          <span className="chat-artifact-title-text">{title || 'JSON'}</span>
                                          {typeof itemCount === 'number' && (
                                            <span style={{ marginLeft: 8, color: 'var(--theme-textSecondary)', fontSize: '0.85em' }}>
                                              {itemCount} item{itemCount === 1 ? '' : 's'}
                                            </span>
                                          )}
                                        </div>
                                        <div className="chat-artifact-actions">
                                          <Tooltip title="Copy JSON">
                                            <Button type="text" size="small" icon={<CopyOutlined />}
                                              onClick={() => { navigator.clipboard.writeText(jsonText); antMessage.success('JSON copied'); }} />
                                          </Tooltip>
                                          <Tooltip title="Full Screen">
                                            <Button type="text" size="small" icon={<FullscreenOutlined />}
                                              onClick={() => setFullscreenModal({ visible: true, type: 'json', content: jsonText, title: title || 'JSON' })} />
                                          </Tooltip>
                                          {sql && (
                                            <Dropdown
                                              trigger={['click']}
                                              menu={{ items: [
                                                {
                                                  key: 'sql',
                                                  icon: <CodeOutlined />,
                                                  label: 'View SQL',
                                                  onClick: () => setSqlModal({ visible: true, sql, title: (title || 'JSON') + ' — SQL' }),
                                                },
                                              ] }}
                                            >
                                              <Tooltip title="More">
                                                <Button type="text" size="small" icon={<MoreOutlined />} />
                                              </Tooltip>
                                            </Dropdown>
                                          )}
                                        </div>
                                      </div>
                                      <div className="chat-artifact-body">
                                        <pre style={{
                                          margin: 0,
                                          background: 'var(--theme-bgSecondary, #f6f8fa)',
                                          padding: '12px',
                                          borderRadius: '6px',
                                          fontSize: '0.85em',
                                          maxHeight: '400px',
                                          overflow: 'auto',
                                          whiteSpace: 'pre',
                                        }}>{jsonText}</pre>
                                      </div>
                                    </div>
                                  );
                                };

                                // Path 1: agent-picked artifacts (multi or empty) from the
                                // backend `delivery_items` event
                                if (Array.isArray(message.artifacts)) {
                                  if (message.artifacts.length === 0) return null;
                                  const tablesById = {};
                                  message.artifacts.forEach(it => {
                                    if (it.type === 'table' && it.id) tablesById[it.id] = it;
                                  });
                                  return (
                                    <>
                                      {message.artifacts.map((it, i) => {
                                        const slotKey = `${it.type}-${it.id || i}`;
                                        if (it.type === 'table') {
                                          return renderTableSlot({
                                            table: { columns: it.columns, rows: it.rows, row_count: it.row_count, truncated: it.truncated, data_summary: it.data_summary, sanity_warnings: it.sanity_warnings },
                                            sql: it.sql,
                                            title: it.title,
                                            key: slotKey,
                                          });
                                        }
                                        if (it.type === 'chart') {
                                          const linkedTable = it.linked_data_id ? tablesById[it.linked_data_id] : null;
                                          // Pass full table (incl. data_summary + sanity_warnings) so the chart's
                                          // [Table] / [Quick Stats] buttons surface meaningful content.
                                          const tableForChart = linkedTable
                                            ? { columns: linkedTable.columns, rows: linkedTable.rows, row_count: linkedTable.row_count, truncated: linkedTable.truncated, data_summary: linkedTable.data_summary, sanity_warnings: linkedTable.sanity_warnings }
                                            : null;
                                          return renderChartSlot({
                                            // The agent ships chart specs as `plotly_figure` (current
                                            // create_chart output). Older flows used `vega_lite_spec`;
                                            // fall back to it so legacy messages still render.
                                            spec: it.plotly_figure || it.vega_lite_spec,
                                            sql: linkedTable ? linkedTable.sql : (it.sql || null),
                                            table: tableForChart,
                                            title: it.title,
                                            key: slotKey,
                                          });
                                        }
                                        if (it.type === 'json') {
                                          return renderJsonSlot({
                                            jsonData: it.json_data,
                                            sql: it.sql,
                                            title: it.title,
                                            itemCount: it.item_count,
                                            key: slotKey,
                                          });
                                        }
                                        return null;
                                      })}
                                    </>
                                  );
                                }

                                // Path 2: legacy fallback — extract from tools (last table + last chart)
                                let dataTable = null;
                                let chartSpec = null;
                                let sqlQuery = null;

                                message.tools?.forEach(tool => {
                                  if (tool.result) {
                                    const result = tool.result;

                                    if (!sqlQuery) {
                                      if (result.sql) sqlQuery = result.sql;
                                      else if (result.metadata?.sql_query) sqlQuery = result.metadata.sql_query;
                                      else if (result.metadata?.sql) sqlQuery = result.metadata.sql;
                                      else if (result.data?.sql) sqlQuery = result.data.sql;
                                      else if ((tool.name === 'fetch_data') && tool.input?.sql) sqlQuery = tool.input.sql;
                                    }

                                    if (!dataTable) {
                                      if (result.data) {
                                        if (result.data.columns && result.data.rows) {
                                          dataTable = result.data;
                                        } else if (result.data.data && result.data.data.columns) {
                                          dataTable = result.data.data;
                                        }
                                      }
                                    }

                                    if (!chartSpec) {
                                      if (result.chart) chartSpec = result.chart;
                                      else if (result.vega_spec) chartSpec = result.vega_spec;
                                      else if (result.vega_lite_spec) chartSpec = result.vega_lite_spec;
                                      else if (result.data?.vega_lite_spec) chartSpec = result.data.vega_lite_spec;
                                      else if (result.data?.chart) chartSpec = result.data.chart;
                                      else if (result.metadata?.chart) chartSpec = result.metadata.chart;
                                      // Plotly figures from create_chart / finish_task tool results
                                      else if (result.plotly_figure) chartSpec = result.plotly_figure;
                                      else if (result.data?.plotly_figure) chartSpec = result.data.plotly_figure;
                                      else if (result.metadata?.plotly_figure) chartSpec = result.metadata.plotly_figure;
                                    }
                                  }
                                });

                                // Legacy fallback: use message-level data_summary / sanity_warnings
                                // (which were attached to the LAST fetch_data result by the agent).
                                const legacyHasStats = !!(message.dataSummary || (message.sanityWarnings && message.sanityWarnings.length > 0));
                                return (
                                  <>
                                    {dataTable && (
                                      <div className="chat-artifact-card">
                                        <div className="chat-artifact-header">
                                          <div className="chat-artifact-title">
                                            <TableOutlined />
                                            <span className="chat-artifact-title-text">Data Table</span>
                                          </div>
                                          <div className="chat-artifact-actions">
                                            {sqlQuery && renderAddToDashboardBtn({
                                              item_type: 'table',
                                              title: 'Data Table',
                                              sql_query: sqlQuery,
                                            }, { iconOnly: true })}
                                            <Tooltip title="Full Screen">
                                              <Button type="text" size="small" icon={<FullscreenOutlined />}
                                                onClick={() => { setFullscreenChartReady(false); setFullscreenModal({ visible: true, type: 'table', content: dataTable, title: 'Data Table' }); }} />
                                            </Tooltip>
                                            <Dropdown
                                              trigger={['click']}
                                              menu={{ items: [
                                                {
                                                  key: 'sql',
                                                  icon: <CodeOutlined />,
                                                  label: 'View SQL',
                                                  disabled: !sqlQuery,
                                                  onClick: () => setSqlModal({ visible: true, sql: sqlQuery, title: 'Data Table — SQL' }),
                                                },
                                              ] }}
                                            >
                                              <Tooltip title="More">
                                                <Button type="text" size="small" icon={<MoreOutlined />} />
                                              </Tooltip>
                                            </Dropdown>
                                          </div>
                                        </div>
                                        <div className="chat-artifact-body">
                                          {dataTable.columns && dataTable.rows ? (
                                            <DataTableWithWhereFilter
                                              initialData={{ columns: dataTable.columns, rows: dataTable.rows, row_count: dataTable.row_count, truncated: dataTable.truncated }}
                                              sqlQuery={sqlQuery}
                                              dataSummary={message.dataSummary}
                                              maxRows={200}
                                              filterValues={messageFilterValues[index]}
                                              onFilterValuesChange={(vals) => setMessageFilterValues(prev => ({ ...prev, [index]: vals }))}
                                            />
                                          ) : (
                                            <div style={{ fontSize: '0.85em', color: 'var(--theme-textSecondary)', overflow: 'auto', maxHeight: '400px' }}>
                                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(dataTable, null, 2)}
                                              </pre>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {chartSpec && (
                                      <div className="chat-artifact-card">
                                        <div className="chat-artifact-header">
                                          <div className="chat-artifact-title">
                                            <BarChartOutlined />
                                            <span className="chat-artifact-title-text">Visualization</span>
                                          </div>
                                          <div className="chat-artifact-actions">
                                            {sqlQuery && (() => {
                                              const specForSave = typeof chartSpec === 'string'
                                                ? JSON.parse(chartSpec)
                                                : { ...chartSpec };
                                              // Preserve Plotly traces (`data` array); strip Vega-Lite inline data.
                                              if (!Array.isArray(specForSave.data)) delete specForSave.data;
                                              return renderAddToDashboardBtn({
                                                item_type: 'chart',
                                                title: specForSave.title || 'Visualization',
                                                sql_query: sqlQuery,
                                                chart_config: specForSave,
                                                grid_w: 6,
                                                grid_h: 6,
                                              }, { iconOnly: true });
                                            })()}
                                            <Tooltip title="Full Screen">
                                              <Button type="text" size="small" icon={<FullscreenOutlined />}
                                                onClick={() => { setFullscreenChartReady(false); setFullscreenModal({ visible: true, type: 'chart', content: chartSpec, title: 'Visualization' }); }} />
                                            </Tooltip>
                                            <Dropdown
                                              trigger={['click']}
                                              menu={{ items: [
                                                {
                                                  key: 'table',
                                                  icon: <DatabaseOutlined />,
                                                  label: 'View underlying data',
                                                  disabled: !dataTable,
                                                  onClick: () => setTableInChartModal({ visible: true, table: dataTable, title: 'Visualization — Underlying data' }),
                                                },
                                                {
                                                  key: 'stats',
                                                  icon: <FundOutlined />,
                                                  label: 'Quick stats',
                                                  disabled: !legacyHasStats,
                                                  onClick: () => setStatsModal({ visible: true, dataSummary: message.dataSummary, sanityWarnings: message.sanityWarnings, title: 'Visualization — Quick Stats' }),
                                                },
                                                {
                                                  key: 'sql',
                                                  icon: <CodeOutlined />,
                                                  label: 'View SQL',
                                                  disabled: !sqlQuery,
                                                  onClick: () => setSqlModal({ visible: true, sql: sqlQuery, title: 'Visualization — SQL' }),
                                                },
                                                {
                                                  key: 'json',
                                                  icon: <CodeOutlined />,
                                                  label: 'View chart JSON',
                                                  onClick: () => setChartJsonModal({ visible: true, spec: chartSpec }),
                                                },
                                              ] }}
                                            >
                                              <Tooltip title="More">
                                                <Button type="text" size="small" icon={<MoreOutlined />} />
                                              </Tooltip>
                                            </Dropdown>
                                          </div>
                                        </div>
                                        <div className="chat-artifact-body">
                                          {isPlotlyFigure(chartSpec)
                                            ? <FilterablePlotly chartSpec={chartSpec} tableData={dataTable} />
                                            : (sqlQuery
                                              ? <DeliveryChartWithFilters chartSpec={chartSpec} initialData={dataTable} sqlQuery={sqlQuery}
                                                  filterValues={messageFilterValues[index]} onFilterValuesChange={(vals) => setMessageFilterValues(prev => ({...prev, [index]: vals}))} />
                                              : <FilterableChart chartSpec={chartSpec} tableData={dataTable} />)}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )
                        }
                      ]}
                    />
                    {/* Feedback buttons: Like, Dislike, Verified */}
                    {!message.error && message.qaRecordId && (
                      <div style={{
                        display: 'flex', gap: '8px', marginTop: '8px', paddingTop: '8px',
                        borderTop: '1px solid #f0f0f0', alignItems: 'center',
                      }}>
                        {/* Like button */}
                        <Tooltip title={messageFeedback[index]?.feedbackType === 'like' ? 'Remove like' : 'Like this answer'}>
                          <Button
                            type="text"
                            size="small"
                            className="feedback-btn feedback-like"
                            disabled={!!feedbackPending[index]}
                            icon={messageFeedback[index]?.feedbackType === 'like' ? <LikeFilled /> : <LikeOutlined />}
                            onClick={async () => {
                              if (feedbackPending[index]) return; // debounce: ignore rapid clicks
                              const current = messageFeedback[index]?.feedbackType;
                              const newType = current === 'like' ? null : 'like';
                              setMessageFeedback(prev => ({
                                ...prev,
                                [index]: { ...prev[index], feedbackType: newType }
                              }));
                              setFeedbackPending(prev => ({ ...prev, [index]: true }));
                              try {
                                await submitDataFeedbackType(message.qaRecordId, newType);
                              } catch (err) {
                                console.error('Feedback failed:', err);
                                // roll back optimistic update so UI reflects reality
                                setMessageFeedback(prev => ({
                                  ...prev,
                                  [index]: { ...prev[index], feedbackType: current }
                                }));
                                antMessage.error('Failed to submit feedback');
                              } finally {
                                setFeedbackPending(prev => ({ ...prev, [index]: false }));
                              }
                            }}
                            style={{
                              color: messageFeedback[index]?.feedbackType === 'like' ? '#1677ff' : '#8c8c8c',
                              fontSize: '14px',
                            }}
                          />
                        </Tooltip>
                        {/* Dislike button */}
                        <Tooltip title={messageFeedback[index]?.feedbackType === 'dislike' ? 'Remove dislike' : 'Dislike this answer'}>
                          <Button
                            type="text"
                            size="small"
                            className="feedback-btn feedback-dislike"
                            disabled={!!feedbackPending[index]}
                            icon={messageFeedback[index]?.feedbackType === 'dislike' ? <DislikeFilled /> : <DislikeOutlined />}
                            onClick={async () => {
                              if (feedbackPending[index]) return;
                              const current = messageFeedback[index]?.feedbackType;
                              const newType = current === 'dislike' ? null : 'dislike';
                              setMessageFeedback(prev => ({
                                ...prev,
                                [index]: { ...prev[index], feedbackType: newType }
                              }));
                              setFeedbackPending(prev => ({ ...prev, [index]: true }));
                              try {
                                await submitDataFeedbackType(message.qaRecordId, newType);
                              } catch (err) {
                                console.error('Feedback failed:', err);
                                setMessageFeedback(prev => ({
                                  ...prev,
                                  [index]: { ...prev[index], feedbackType: current }
                                }));
                                antMessage.error('Failed to submit feedback');
                              } finally {
                                setFeedbackPending(prev => ({ ...prev, [index]: false }));
                              }
                            }}
                            style={{
                              color: messageFeedback[index]?.feedbackType === 'dislike' ? '#ff4d4f' : '#8c8c8c',
                              fontSize: '14px',
                            }}
                          />
                        </Tooltip>
                        {/* Verified button — Admin only */}
                        {isAdmin && (
                          <Tooltip title={messageFeedback[index]?.verified ? 'Verified ✅' : 'Mark as Verified (adds to Data Gallery)'}>
                            <Button
                              type="text"
                              size="small"
                              icon={messageFeedback[index]?.verified ? <CheckCircleFilled /> : <CheckCircleOutlined />}
                              onClick={async () => {
                                const wasVerified = messageFeedback[index]?.verified;
                                setMessageFeedback(prev => ({
                                  ...prev,
                                  [index]: { ...prev[index], verified: !wasVerified }
                                }));
                                try {
                                  await markDataQAAnswer(message.qaRecordId, !wasVerified);
                                  antMessage.success(!wasVerified ? 'Answer verified & added to Gallery' : 'Verification removed');
                                } catch (err) {
                                  console.error('Verify failed:', err);
                                  setMessageFeedback(prev => ({
                                    ...prev,
                                    [index]: { ...prev[index], verified: wasVerified }
                                  }));
                                  antMessage.error('Failed to update verification');
                                }
                              }}
                              style={{
                                color: messageFeedback[index]?.verified ? '#52c41a' : '#8c8c8c',
                                fontSize: '14px',
                              }}
                            >
                              {messageFeedback[index]?.verified && (
                                <span style={{ marginLeft: 4, fontSize: 12 }}>Verified</span>
                              )}
                            </Button>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}

            {/* Current streaming response */}
            {isLoading && (
              <div className="message assistant">
                <div className="message-header">
                  <span className="message-role">Assistant</span>
                </div>
                <Tabs
                  activeKey={streamingTab}
                  onChange={setStreamingTab}
                  items={[
                    {
                      key: 'progress',
                      label: 'Progress',
                      children: (
                        <div className="tab-content">
                          {/* Live process metrics */}
                          <div style={{ 
                            display: 'flex', gap: '16px', marginBottom: '12px', padding: '8px 12px', 
                            background: 'var(--theme-cardBgMuted)', 
                            borderRadius: '6px', fontSize: '0.85em', color: 'var(--theme-textSecondary, #595959)',
                            border: '1px solid var(--theme-cardBorder)', alignItems: 'center', flexWrap: 'wrap'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <ClockCircleOutlined spin style={{ color: 'var(--theme-primary)' }} />
                              <strong>{(elapsedTime / 1000).toFixed(1)}s</strong> total
                            </span>
                            {toolExecutions.length > 0 && (() => {
                              const totalToolMs = toolExecutions.reduce((sum, t) => sum + (t.elapsedMs || 0), 0);
                              const thinkingMs = Math.max(0, elapsedTime - totalToolMs);
                              return (
                                <>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8c8c8c' }}>
                                    🧠 <strong style={{ color: '#595959' }}>{(thinkingMs / 1000).toFixed(1)}s</strong> thinking
                                  </span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8c8c8c' }}>
                                    🔧 <strong style={{ color: '#595959' }}>{(totalToolMs / 1000).toFixed(1)}s</strong> tools
                                  </span>
                                </>
                              );
                            })()}
                            {(streamingMetrics.inputTokens > 0 || streamingMetrics.outputTokens > 0) && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                📊 <strong>{(streamingMetrics.inputTokens + streamingMetrics.outputTokens).toLocaleString()}</strong> tokens
                                <span style={{ color: '#8c8c8c', fontSize: '0.9em' }}>
                                  ({streamingMetrics.inputTokens.toLocaleString()} in / {streamingMetrics.outputTokens.toLocaleString()} out)
                                </span>
                              </span>
                            )}
                          </div>
                          {toolExecutions.length > 0 ? (
                            toolExecutions.map((tool, idx) => {
                              // Calculate per-step thinking time (live)
                              const prevToolEnd = idx > 0 ? (toolExecutions[idx - 1].endTime || toolExecutions[idx - 1].startTime + (toolExecutions[idx - 1].elapsedMs || 0)) : null;
                              const thinkingMs = tool.startTime ? (prevToolEnd ? tool.startTime - prevToolEnd : (processStartTimeRef.current ? tool.startTime - processStartTimeRef.current : null)) : null;
                              return (
                              <div key={idx} className="tool-execution" style={{ marginBottom: '12px', padding: '10px 12px', background: 'var(--theme-cardBgMuted)', borderRadius: '4px', borderLeft: '3px solid var(--theme-primary)' }}>
                                {tool.agentMessage && (
                                  <div style={{ marginBottom: '8px', padding: '7px 10px', background: '#f5f0e8', borderRadius: '4px', borderLeft: '3px solid #e8c870', color: '#595959', fontSize: '0.95em' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px' }}>
                                      <span style={{ fontSize: '13px', color: '#b8860b', fontWeight: 600 }}>🧠 Agent reasoning</span>
                                      {thinkingMs != null && thinkingMs > 0 && (
                                        <span style={{ fontSize: '13px', color: '#8c8c8c' }}>🧠 {(thinkingMs / 1000).toFixed(1)}s thinking</span>
                                      )}
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{tool.agentMessage}</ReactMarkdown>
                                  </div>
                                )}
                                <div className="tool-name" style={{ marginBottom: '6px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                  <span>🔧 <strong>{tool.tool || tool.name}</strong></span>
                                  <span className={`tool-status tool-status-${tool.status || 'processing'}`}>
                                    {tool.status || 'processing'}
                                  </span>
                                  {(tool.elapsedMs || tool.inputTokens > 0) && (
                                    <span style={{ fontSize: '0.8em', color: '#8c8c8c', marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                                      {tool.elapsedMs != null && (
                                        <span>⏱ {(tool.elapsedMs / 1000).toFixed(1)}s</span>
                                      )}
                                      {(tool.inputTokens > 0 || tool.outputTokens > 0) && (
                                        <span>🔤 {((tool.inputTokens || 0) + (tool.outputTokens || 0)).toLocaleString()} tok</span>
                                      )}
                                    </span>
                                  )}
                                </div>
                                {(tool.input || tool.result) && (
                                  <Collapse
                                    size="small"
                                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} style={{ color: '#8c8c8c' }} />}
                                    style={{ background: 'transparent', border: 'none' }}
                                    items={[
                                      ...(tool.input ? [{
                                        key: 'input',
                                        label: <span style={{ fontSize: '0.83em', fontWeight: 600, color: '#666' }}>📥 Input</span>,
                                        children: (
                                          <div className="tool-details" style={{ fontSize: '0.83em', color: '#666', background: '#fff', padding: '7px', borderRadius: '4px', maxHeight: '150px', overflow: 'auto' }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{JSON.stringify(tool.input, null, 2)}</pre>
                                          </div>
                                        ),
                                        style: { border: 'none', padding: 0 }
                                      }] : []),
                                      ...(tool.result ? [{
                                        key: 'output',
                                        label: <span style={{ fontSize: '0.83em', fontWeight: 600, color: '#666' }}>📤 Output</span>,
                                        children: (
                                          <div className="tool-details" style={{ fontSize: '0.83em', color: '#666', background: '#fff', padding: '7px', borderRadius: '4px', maxHeight: '200px', overflow: 'auto' }}>
                                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{typeof tool.result === 'string' ? tool.result : JSON.stringify(tool.result, null, 2)}</pre>
                                          </div>
                                        ),
                                        style: { border: 'none', padding: 0 }
                                      }] : [])
                                    ]}
                                  />
                                )}
                              </div>
                              );
                            })
                          ) : (
                            <div><Spin size="small" /> Processing...</div>
                          )}
                        </div>
                      )
                    },
                    {
                      key: 'artifacts',
                      label: 'Artifacts',
                      children: (
                        <div className="tab-content">
                          {(() => {
                            // Aggregate deliverables from streaming tools
                            const deliverables = {
                              sql: null,
                              data: null,
                              chart: null,
                              confidence: null,
                              ontologyMermaid: null,
                              sources: []
                            };

                            toolExecutions.forEach(tool => {
                              if (tool.result) {
                                const result = tool.result;
                                const toolName = tool.tool || tool.name;
                                
                                // Extract SQL
                                if (result.sql) deliverables.sql = result.sql;
                                if (result.metadata?.sql_query) deliverables.sql = result.metadata.sql_query;
                                if (result.metadata?.sql) deliverables.sql = result.metadata.sql;
                                if (result.data?.sql) deliverables.sql = result.data.sql;
                                // fetch_data: use tool.input.sql as the most direct source
                                if (toolName === 'fetch_data' && tool.input?.sql) deliverables.sql = tool.input.sql;
                                
                                // Extract data/table
                                if (result.data) deliverables.data = result.data;
                                
                                // Extract chart — only accept objects with actual spec keys
                                const isValidChart = (c) => c && typeof c === 'object' && !Array.isArray(c) && Object.keys(c).length > 0;
                                if (isValidChart(result.chart)) deliverables.chart = result.chart;
                                if (isValidChart(result.vega_spec)) deliverables.chart = result.vega_spec;
                                if (isValidChart(result.vega_lite_spec)) deliverables.chart = result.vega_lite_spec;
                                if (isValidChart(result.plotly_figure)) deliverables.chart = result.plotly_figure;
                                if (isValidChart(result.data?.plotly_figure)) deliverables.chart = result.data.plotly_figure;
                                if (typeof result.chart === 'string' && result.chart.length > 2) deliverables.chart = result.chart;

                                // Extract ontology mermaid diagram
                                // From manager-level delivery (assign_task_to_data_agent / answer tool results)
                                if (result.delivery?.data?.ontology_mermaid) {
                                  deliverables.ontologyMermaid = result.delivery.data.ontology_mermaid;
                                }
                                // Direct data path (when tool result contains data.ontology_mermaid)
                                if (result.data?.ontology_mermaid) {
                                  deliverables.ontologyMermaid = result.data.ontology_mermaid;
                                }

                                // Extract confidence
                                if (result.confidence) deliverables.confidence = result.confidence;
                                if (result.metadata?.confidence) deliverables.confidence = result.metadata.confidence;
                                
                                // Extract sources
                                if (toolName?.includes('search') || toolName?.includes('knowledge')) {
                                  if (typeof result === 'string') {
                                    deliverables.sources.push(result);
                                  } else if (result.sources) {
                                    deliverables.sources.push(...result.sources);
                                  } else if (result.results) {
                                    deliverables.sources.push(...result.results);
                                  }
                                }
                              }
                            });

                            const hasDeliverables = deliverables.sql || deliverables.data || deliverables.chart || 
                                                   deliverables.confidence || deliverables.ontologyMermaid ||
                                                   deliverables.sources.length > 0;

                            if (!hasDeliverables) {
                              return <div style={{ color: '#8c8c8c' }}><Spin size="small" /> Waiting for artifacts...</div>;
                            }

                            return (
                              <div>
                                {deliverables.ontologyMermaid && (
                                  <div className="chat-artifact-card">
                                    <div className="chat-artifact-header">
                                      <div className="chat-artifact-title">
                                        <FileSearchOutlined />
                                        <span className="chat-artifact-title-text">Semantic Relationship Diagram</span>
                                      </div>
                                    </div>
                                    <div className="chat-artifact-body">
                                      <MermaidViewer code={deliverables.ontologyMermaid} height={400} />
                                    </div>
                                  </div>
                                )}

                                {deliverables.sql && (
                                  <div className="chat-artifact-card">
                                    <div className="chat-artifact-header">
                                      <div className="chat-artifact-title">
                                        <CodeOutlined />
                                        <span className="chat-artifact-title-text">SQL Query</span>
                                      </div>
                                    </div>
                                    <div className="chat-artifact-body">
                                      <pre style={{ fontSize: '0.85em', background: 'var(--theme-backgroundSecondary)', padding: '8px', borderRadius: '4px', overflow: 'auto', margin: 0, maxHeight: '150px' }}>
                                        {deliverables.sql}
                                      </pre>
                                    </div>
                                  </div>
                                )}

                                {deliverables.data && (
                                  <div className="chat-artifact-card">
                                    <div className="chat-artifact-header">
                                      <div className="chat-artifact-title">
                                        <TableOutlined />
                                        <span className="chat-artifact-title-text">Data Preview</span>
                                      </div>
                                    </div>
                                    <div className="chat-artifact-body" style={{ fontSize: '0.85em', color: 'var(--theme-textSecondary)' }}>
                                      {typeof deliverables.data === 'string'
                                        ? deliverables.data.substring(0, 300)
                                        : JSON.stringify(deliverables.data).substring(0, 300)}...
                                    </div>
                                  </div>
                                )}

                                {deliverables.chart && (
                                  <div className="chat-artifact-card">
                                    <div className="chat-artifact-header">
                                      <div className="chat-artifact-title">
                                        <BarChartOutlined />
                                        <span className="chat-artifact-title-text">Chart Generated</span>
                                      </div>
                                    </div>
                                    <div className="chat-artifact-body" style={{ fontSize: '0.85em', color: 'var(--theme-textSecondary)' }}>
                                      Chart specification ready
                                    </div>
                                  </div>
                                )}

                                {deliverables.confidence && (
                                  <div className="chat-artifact-card">
                                    <div className="chat-artifact-header">
                                      <div className="chat-artifact-title">
                                        <SafetyCertificateOutlined />
                                        <span className="chat-artifact-title-text">Confidence</span>
                                      </div>
                                    </div>
                                    <div className="chat-artifact-body" style={{ fontSize: '0.9em', color: 'var(--theme-textSecondary)' }}>
                                      {typeof deliverables.confidence === 'string'
                                        ? deliverables.confidence
                                        : JSON.stringify(deliverables.confidence).substring(0, 200)}
                                    </div>
                                  </div>
                                )}

                                {deliverables.sources.length > 0 && (
                                  <div className="chat-artifact-card">
                                    <div className="chat-artifact-header">
                                      <div className="chat-artifact-title">
                                        <FileSearchOutlined />
                                        <span className="chat-artifact-title-text">Sources Found</span>
                                      </div>
                                    </div>
                                    <div className="chat-artifact-body" style={{ fontSize: '0.9em', color: 'var(--theme-textSecondary)' }}>
                                      {deliverables.sources.length} source(s) retrieved
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )
                    },
                    {
                      key: 'answer',
                      label: 'Final Answer',
                      children: (
                        <div className="tab-content">
                          {currentResponse ? (
                            // XSS-safe: no rehypeRaw (TC-DC-EC-10).
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {currentResponse}
                            </ReactMarkdown>
                          ) : (
                            <div><Spin size="small" /> Generating answer...</div>
                          )}
                        </div>
                      )
                    }
                  ]}
                />
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="input-container-chat">
        {chatMode === 'pro-editor' && (
          <div
            className="chat-mode-banner chat-mode-banner--pro"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', margin: '0 0 6px 0',
              borderRadius: 8,
              background: 'linear-gradient(90deg, rgba(196,154,60,0.12), rgba(196,154,60,0.04))',
              border: '1px solid rgba(196,154,60,0.35)',
              color: '#7a5b1d', fontSize: 12, fontWeight: 500,
            }}
          >
            <span style={{ fontSize: 14 }}>✨</span>
            <span>
              <strong>Pro Editor mode.</strong>
              {' '}The Data Agent is focused on the chart editor —
              ask it to tweak the SQL or the chart and it will apply the change in place.
            </span>
          </div>
        )}
        {filterEditorContext && (
          <div
            className="chat-mode-banner chat-mode-banner--filter-editor"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 12px', margin: '0 0 6px 0',
              borderRadius: 8,
              background: 'linear-gradient(90deg, rgba(24,144,255,0.12), rgba(24,144,255,0.04))',
              border: '1px solid rgba(24,144,255,0.35)',
              color: '#0d5fb3', fontSize: 12, fontWeight: 500,
            }}
          >
            <span style={{ fontSize: 14 }}>🎯</span>
            <span>
              <strong>
                {filterEditorContext.mode === 'edit' ? 'Editing dashboard filter' : 'New dashboard filter'}
                {filterEditorContext.draft?.label ? `: "${filterEditorContext.draft.label}"` : ''}.
              </strong>
              {' '}The Data Agent is focused on this dialog — describe the filter and it will fill it in or update it for you.
            </span>
          </div>
        )}
        {focusedComponent && isDashboardOpen && (
          <div className="focused-component-indicator">
            <span className="focused-component-label">
              ⭐ Focusing: <strong>{focusedComponent.title}</strong>
            </span>
            {onClearFocus && (
              <button className="focused-component-clear" onClick={onClearFocus} title="Clear focus">×</button>
            )}
          </div>
        )}
        <div className="input-wrapper-chat">
          {isAdmin && (
            <Tooltip title="Prompt Library">
              <Button
                type="text"
                icon={<BulbOutlined style={{ fontSize: '18px', color: '#8c8c8c' }} />}
                onClick={() => setPromptLibraryVisible(true)}
                style={{ flexShrink: 0 }}
              />
            </Tooltip>
          )}
          <TextArea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            autoSize={{ minRows: 1, maxRows: 10 }}
            disabled={isLoading}
            bordered={false}
            className="chat-input"
          />
          <Button
            type="text"
            icon={
              <SendOutlined
                style={{
                  fontSize: 18,
                  color: input.trim() && !isLoading ? 'var(--theme-primary)' : 'var(--theme-textIcon)',
                  background: input.trim() && !isLoading ? 'var(--theme-shadowStrong)' : 'transparent',
                  padding: 8,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: input.trim() && !isLoading ? '0 2px 8px var(--theme-shadow)' : 'none',
                  opacity: input.trim() ? 1 : 0.45,
                  transition: 'all 0.25s',
                }}
              />
            }
            onClick={handleSend}
            loading={isLoading}
            disabled={!input.trim()}
            className="send-button-chat"
          />
        </div>
      </div>

      {/* Confidence Assessment Modal */}
      <Modal
        title={null}
        open={confidenceModal.visible}
        onCancel={() => setConfidenceModal({ visible: false, confidence: null, breakdown: null })}
        footer={<Button type="primary" onClick={() => setConfidenceModal({ visible: false, confidence: null, breakdown: null })}>Close</Button>}
        width={520}
      >
        {confidenceModal.visible && (() => {
          const conf = confidenceModal.confidence;
          const confObj = typeof conf === 'object' ? conf : null;
          const bd = confidenceModal.breakdown || confObj;
          const pct = typeof conf === 'number' ? conf * 100 : (confObj?.score ? confObj.score * 100 : 0);
          const level = bd?.level || '';
          const gaugeColor = pct >= 85 ? '#52c41a' : pct >= 70 ? '#faad14' : '#ff4d4f';
          const summaryText = bd?.summary || '';
          const breakdown = bd?.breakdown || {};
          const getBarColor = (score) => score >= 8 ? '#52c41a' : score >= 6 ? '#faad14' : '#ff4d4f';
          const factorMeta = {
            requirement_clarity: { icon: <FileSearchOutlined />, label: 'Requirement Clarity' },
            query_reliability: { icon: <DatabaseOutlined />, label: 'Query Reliability' },
            data_fitness: { icon: <FundOutlined />, label: 'Data Fitness' },
          };
          return (
            <div style={{ padding: '4px 0' }}>
              {/* Header: score + level + verified */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px' }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: `conic-gradient(${gaugeColor} ${pct * 3.6}deg, #f0f0f0 0deg)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <div style={{
                    width: 60, height: 60, borderRadius: '50%', background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <span style={{ fontSize: '1.5em', fontWeight: 700, color: gaugeColor }}>{Math.round(pct)}%</span>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 700, fontSize: '1.1em', color: '#262626' }}>
                      Confidence: <span style={{ color: gaugeColor, textTransform: 'uppercase' }}>{level}</span>
                    </span>
                    {bd?.verified_by_admin && (
                      <Tooltip title={`Near-exact match to admin-verified Q&A: "${bd.verified_match_label}" (${Math.round((bd.verified_match_similarity || 0) * 100)}% similarity)`}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '1px 8px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}>
                          <SafetyCertificateOutlined style={{ color: '#52c41a', fontSize: '0.85em' }} />
                          <span style={{ fontSize: '0.75em', fontWeight: 600, color: '#389e0d' }}>Verified</span>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                  {summaryText && (
                    <div style={{ lineHeight: '1.4' }}>
                      <span style={{ fontWeight: 700, color: '#262626' }}>Reason: </span>
                      <span style={{ color: '#595959' }}>{summaryText}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Collapsible factor breakdown */}
              <Collapse
                ghost
                size="small"
                style={{ marginTop: '8px' }}
                items={[{
                  key: 'breakdown',
                  label: <span style={{ fontSize: '0.88em', fontWeight: 500, color: '#1677ff' }}>See breakdowns</span>,
                  children: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingTop: '4px' }}>
                      {Object.entries(breakdown).map(([factor, details]) => {
                        const score = details.score || 0;
                        const pctBar = score * 10;
                        const meta = factorMeta[factor] || { icon: null, label: factor.replace(/_/g, ' ') };
                        const weightPct = Math.round((details.weight || 0) * 100);
                        return (
                          <div key={factor}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, fontSize: '0.92em', color: '#434343' }}>
                                <span style={{ color: getBarColor(score), fontSize: '1.05em' }}>{meta.icon}</span>
                                {meta.label}
                                <span style={{ fontSize: '0.82em', color: '#bfbfbf', fontWeight: 400 }}>({weightPct}%)</span>
                              </span>
                              <span style={{ fontSize: '0.88em', fontWeight: 700, color: getBarColor(score) }}>{score}/10</span>
                            </div>
                            <Progress percent={pctBar} size="small" strokeColor={getBarColor(score)} showInfo={false} style={{ marginBottom: '4px' }} />
                            {details.reason && (
                              <div style={{ fontSize: '0.85em', color: '#8c8c8c', lineHeight: '1.45' }}>{details.reason}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ),
                }]}
              />

              {/* Gallery boost */}
              {bd?.gallery_boost > 0 && (
                <div style={{ marginTop: '10px', padding: '8px 12px', background: 'var(--theme-cardBgMuted)', borderRadius: '6px', fontSize: '0.82em', color: 'var(--theme-textSecondary, #8c8c8c)' }}>
                  <span style={{ fontWeight: 500 }}>Gallery Boost:</span> +{(bd.gallery_boost * 100).toFixed(1)}% from {bd.gallery_match_count > 1 ? `${bd.gallery_match_count} verified answers` : 'verified answer'}
                  {bd.gallery_best_match && <> — "{bd.gallery_best_match}"</>}
                  {' '}({Math.round((bd.gallery_best_similarity || 0) * 100)}% match)
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {/* Chart JSON Modal */}
      <Modal
        title={<span><CodeOutlined style={{ marginRight: 8, color: '#fa8c16' }} />Chart JSON Specification</span>}
        open={chartJsonModal.visible}
        onCancel={() => setChartJsonModal({ visible: false, spec: null })}
        footer={[
          <Button key="copy" onClick={() => {
            navigator.clipboard.writeText(
              typeof chartJsonModal.spec === 'string'
                ? chartJsonModal.spec
                : JSON.stringify(chartJsonModal.spec, null, 2)
            );
          }}>Copy</Button>,
          <Button key="close" type="primary" onClick={() => setChartJsonModal({ visible: false, spec: null })}>Close</Button>
        ]}
        width={720}
      >
        <pre style={{ maxHeight: '60vh', overflow: 'auto', fontSize: '0.8em', background: '#f6f8fa', padding: '16px', borderRadius: '6px', margin: 0 }}>
          {chartJsonModal.spec
            ? (typeof chartJsonModal.spec === 'string' ? chartJsonModal.spec : JSON.stringify(chartJsonModal.spec, null, 2))
            : ''}
        </pre>
      </Modal>

      {/* Quick Stats attachment modal — opens from a button on the table/chart header */}
      <Modal
        title={<span><FundOutlined style={{ marginRight: 8, color: '#1677ff' }} />{statsModal.title || 'Quick Stats'}</span>}
        open={statsModal.visible}
        onCancel={() => setStatsModal({ visible: false, dataSummary: null, sanityWarnings: null, title: '' })}
        footer={[<Button key="close" type="primary" onClick={() => setStatsModal({ visible: false, dataSummary: null, sanityWarnings: null, title: '' })}>Close</Button>]}
        width={920}
      >
        {(statsModal.dataSummary || (statsModal.sanityWarnings && statsModal.sanityWarnings.length > 0))
          ? <QuickStatsPanel dataSummary={statsModal.dataSummary} sanityWarnings={statsModal.sanityWarnings} />
          : <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>No statistics available for this artifact.</div>}
      </Modal>

      {/* SQL attachment modal */}
      <Modal
        title={<span><CodeOutlined style={{ marginRight: 8, color: '#722ed1' }} />{sqlModal.title || 'SQL Query'}</span>}
        open={sqlModal.visible}
        onCancel={() => setSqlModal({ visible: false, sql: '', title: '' })}
        footer={[
          <Button key="copy" icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(sqlModal.sql || '')}>Copy</Button>,
          <Button key="close" type="primary" onClick={() => setSqlModal({ visible: false, sql: '', title: '' })}>Close</Button>
        ]}
        width={780}
      >
        <pre style={{ maxHeight: '60vh', overflow: 'auto', fontSize: '0.85em', background: '#f6f8fa', padding: '16px', borderRadius: '6px', margin: 0, whiteSpace: 'pre-wrap' }}>
          {sqlModal.sql || '(no SQL captured)'}
        </pre>
      </Modal>

      {/* Underlying-table-of-chart modal */}
      <Modal
        title={<span>📋 {tableInChartModal.title || 'Underlying data'}</span>}
        open={tableInChartModal.visible}
        onCancel={() => setTableInChartModal({ visible: false, table: null, title: '' })}
        footer={[<Button key="close" type="primary" onClick={() => setTableInChartModal({ visible: false, table: null, title: '' })}>Close</Button>]}
        width={1000}
      >
        {tableInChartModal.table && tableInChartModal.table.columns && tableInChartModal.table.rows
          ? <AdvancedTable
              data={{ columns: tableInChartModal.table.columns, rows: tableInChartModal.table.rows, row_count: tableInChartModal.table.row_count, truncated: tableInChartModal.table.truncated }}
              dataSummary={tableInChartModal.table.data_summary}
              maxRows={500}
            />
          : <div style={{ color: '#888', textAlign: 'center', padding: 20 }}>Chart has no linked data table.</div>}
      </Modal>

      {/* Fullscreen Modal */}
      <Modal
        title={<span><FullscreenOutlined style={{ marginRight: 8 }} />{fullscreenModal.title}</span>}
        open={fullscreenModal.visible}
        onCancel={() => { setFullscreenModal({ visible: false, type: null, content: null, title: '' }); setFullscreenChartReady(false); }}
        afterOpenChange={(open) => { if (open && fullscreenModal.type === 'chart') setFullscreenChartReady(true); }}
        footer={[
          fullscreenModal.type === 'sql' && (
            <Button key="copy" icon={<CopyOutlined />} onClick={() => navigator.clipboard.writeText(fullscreenModal.content)}>
              Copy SQL
            </Button>
          ),
          fullscreenModal.type === 'table' && fullscreenModal.content && (
            <Button key="csv" icon={<DownloadOutlined />} onClick={() => {
              const { columns, rows } = fullscreenModal.content;
              const escape = v => v == null ? '' : ('"' + String(v).replace(/"/g, '""') + '"');
              const csv = [columns.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
              a.download = 'data.csv'; a.click(); URL.revokeObjectURL(a.href);
            }}>Download CSV</Button>
          ),
          fullscreenModal.type === 'json' && fullscreenModal.content && (
            <Button key="copyjson" icon={<CopyOutlined />} onClick={() => { navigator.clipboard.writeText(fullscreenModal.content); antMessage.success('JSON copied'); }}>
              Copy JSON
            </Button>
          ),
          <Button key="close" type="primary" onClick={() => { setFullscreenModal({ visible: false, type: null, content: null, title: '' }); setFullscreenChartReady(false); }}>Close</Button>
        ].filter(Boolean)}
        width="90vw"
        style={{ top: 20 }}
        styles={{ body: { maxHeight: 'calc(90vh - 120px)', overflow: 'auto', padding: '16px' } }}
      >
        {fullscreenModal.type === 'sql' && (
          <pre style={{ fontSize: '0.9em', background: '#f6f8fa', padding: '16px', borderRadius: '6px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fullscreenModal.content}
          </pre>
        )}
        {fullscreenModal.type === 'table' && fullscreenModal.content?.columns && fullscreenModal.content?.rows && (
          <div>
            <div style={{ marginBottom: '8px', color: '#999', fontSize: '0.9em' }}>
              {fullscreenModal.content.row_count || fullscreenModal.content.rows.length} rows × {fullscreenModal.content.columns.length} columns
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                <thead>
                  <tr style={{ background: '#fafafa', borderBottom: '2px solid #52c41a', position: 'sticky', top: 0 }}>
                    {fullscreenModal.content.columns.map((col, idx) => (
                      <th key={idx} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#262626', whiteSpace: 'nowrap', background: '#fafafa' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fullscreenModal.content.rows.map((row, rowIdx) => (
                    <tr key={rowIdx} style={{ borderBottom: '1px solid #f0f0f0', background: rowIdx % 2 === 0 ? '#fff' : '#fafffe' }}>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} style={{ padding: '8px 12px', color: '#595959' }}>
                          {cell === null ? <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>null</span> : String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {fullscreenModal.type === 'chart' && fullscreenModal.content && (
          <FullscreenChartRenderer spec={fullscreenModal.content} shouldRender={fullscreenChartReady} />
        )}
        {fullscreenModal.type === 'json' && fullscreenModal.content && (
          <pre style={{ fontSize: '0.9em', background: '#f6f8fa', padding: '16px', borderRadius: '6px', margin: 0, whiteSpace: 'pre', overflow: 'auto', maxHeight: '80vh' }}>
            {fullscreenModal.content}
          </pre>
        )}
      </Modal>

      {/* Prompt Library Dialog */}
      <PromptLibraryDialog
        visible={promptLibraryVisible}
        onCancel={() => setPromptLibraryVisible(false)}
        onSelectPrompt={(prompt) => {
          setInput(prompt);
          antMessage.success('Prompt template loaded — you can edit before sending');
        }}
        onTemplateChange={() => {
          // Refresh homepage templates and folders when templates are modified
          Promise.all([
            promptTemplatesService.listTemplates(null),
            promptTemplatesService.listFolders(false),
          ]).then(([all, folders]) => {
            const pinnedTemplates = all.filter(t => t.show_in_homepage);
            setHomepageTemplates(pinnedTemplates);
            setPublishedFolders(folders);
          }).catch(() => {});
        }}
      />
    </div>
  );
});

export default ChatInterface;
